/**
 * MentorsPage.jsx — HealHub Mentor Booking (Production v3.1 — Premium UI Rewrite)
 *
 * LOGIC: 100% unchanged from original v3.1
 *   All fixes preserved: time normalization, join window, RT closure bug,
 *   fetchSessions, confirm done, cancellation, feedback, report, video, chat,
 *   admin booking, toast system, session classification useMemo, etc.
 *
 * UI: Complete premium redesign matching HealHub Login design system
 *   - Fraunces display + Satoshi body (same as Login)
 *   - CSS variables: --brand #5A6993, --peach #E6A184, --sage #7B8F8A,
 *     --bg #F9F4F0, --card #FFFFFF, --ink #2D364D (all from Login/mobile.css)
 *   - Mobile-first: 100dvh, env(safe-area-inset-*), no horizontal overflow
 *   - Mentor cards: soft linen bg, editorial photo treatment, ink typography
 *   - Modals: bottom-sheet on mobile, centered on desktop (same pattern as Login)
 *   - Header: frosted glass, brand gradient, Fraunces headline
 *   - Chips, slots, badges: pill shapes with brand/peach/sage accents
 *   - Animations: staggered card reveals, spring hover, pulse for live sessions
 *   - Dark mode: all variables inverted (matches Login .hh-dark pattern)
 */

import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { supabase }    from "../supabase";
import { auth }        from "../firebase";
import { useNavigate } from "react-router-dom";
import HealHubVideoRoom from "./HealHubVideoRoom";

const BACKEND_URL  = process.env.REACT_APP_BACKEND_URL    || "http://localhost:5000";
const RAZORPAY_KEY = process.env.REACT_APP_RAZORPAY_KEY_ID || "";
const ADMIN_EMAIL  = process.env.REACT_APP_ADMIN_EMAIL    || "";
const PLATFORM_FEE = 10;

const CATEGORIES = [
  {value:"",              label:"All",              icon:"🌈"},
  {value:"mental_health", label:"Mental Health",    icon:"🧠"},
  {value:"career",        label:"Career",           icon:"💼"},
  {value:"relationships", label:"Relationships",    icon:"💑"},
  {value:"stress",        label:"Stress & Anxiety", icon:"🌊"},
  {value:"grief",         label:"Grief & Loss",     icon:"🕊️"},
  {value:"addiction",     label:"Addiction",        icon:"🤝"},
  {value:"parenting",     label:"Parenting",        icon:"👨‍👩‍👧"},
  {value:"student",       label:"Student",          icon:"📚"},
  {value:"general",       label:"General",          icon:"🌿"},
];

const calcCancellationRefund = (sessionPrice, hoursUntil) => {
  if (hoursUntil > 12) return { refund:Math.floor(sessionPrice*0.85), type:"early", fee:Math.floor(sessionPrice*0.15) };
  return { refund:Math.floor(sessionPrice*0.30), type:"late", fee:Math.floor(sessionPrice*0.70) };
};

const formatTimeRemaining = (ms) => {
  if (!ms||ms<=0) return "Ended";
  const m=Math.floor(ms/60000), s=Math.floor((ms%60000)/1000);
  return `${m}:${String(s).padStart(2,"0")}`;
};

const jsDayToDbDay = (d) => d===0?7:d;
const calculateBadge = (s) => s>200?"Top Rated":s>100?"Expert":s>50?"Popular":"Rising";

const convertTo24 = (t12) => {
  if (!t12) return "00:00:00";
  const [time, mod] = t12.split(" ");
  let [h, m] = time.split(":");
  h = parseInt(h,10);
  if (mod==="AM"&&h===12) h=0;
  if (mod==="PM"&&h!==12) h+=12;
  return `${String(h).padStart(2,"0")}:${m}:00`;
};

const normalizeTime = (timeStr) => {
  if (!timeStr) return "00:00:00";
  const clean = String(timeStr).trim().replace(/[+-]\d{2}:\d{2}$/, "").split(".")[0];
  const parts = clean.split(":");
  const h = (parts[0] || "00").padStart(2,"0");
  const m = (parts[1] || "00").padStart(2,"0");
  const s = (parts[2] || "00").padStart(2,"0");
  return `${h}:${m}:${s}`;
};

const buildSessionDate = (dateStr, rawTime) => {
  const t = normalizeTime(rawTime);
  return new Date(`${dateStr}T${t}`);
};

const getAvailableSlots = async (mentorId, date) => {
  if (!mentorId||!date) return [];
  try {
    const selectedDate = new Date(date+"T00:00:00");
    if (isNaN(selectedDate)) return [];
    const {data:dayOff} = await supabase.from("mentor_day_offs")
      .select("date").eq("mentor_id",mentorId).eq("date",date).maybeSingle();
    if (dayOff) return "day_off";
    const dbDay = jsDayToDbDay(selectedDate.getDay());
    const {data:avail} = await supabase.from("mentor_availability")
      .select("*").eq("mentor_id",mentorId).eq("day_of_week",dbDay);
    if (!avail?.length) return [];
    const generated = [];
    avail.forEach(a => {
      const startTime = normalizeTime(a.start_time);
      const endTime   = normalizeTime(a.end_time);
      let cur = new Date(`${date}T${startTime}`);
      const end = new Date(`${date}T${endTime}`);
      while (cur < end) {
        generated.push(cur.toTimeString().slice(0,8));
        cur = new Date(cur.getTime()+3600000);
      }
    });
    const now = new Date();
    const cutoff = new Date(now.getTime()+3600000);
    const isToday = date===now.toISOString().split("T")[0];
    const fifteenAgo = new Date(now.getTime()-900000).toISOString();
    const {data:booked} = await supabase.from("appointments")
      .select("time,status,created_at")
      .eq("mentor_id",mentorId).eq("date",date)
      .not("status","in",'("cancelled_by_user","cancelled_by_mentor","refunded")');
    const bookedTimes = (booked||[])
      .filter(b => !(b.status==="pending_payment"&&b.created_at<fifteenAgo))
      .map(b => normalizeTime(b.time));
    return generated
      .filter(slot => {
        if (bookedTimes.includes(slot)) return false;
        if (isToday && new Date(`${date}T${slot}`) < cutoff) return false;
        return true;
      })
      .map(slot => {
        const [h,m]=slot.split(":");
        let hour=parseInt(h,10);
        const ampm=hour>=12?"PM":"AM";
        if (hour>12) hour-=12;
        if (hour===0) hour=12;
        return `${String(hour).padStart(2,"0")}:${m} ${ampm}`;
      });
  } catch(err) {
    console.error("getAvailableSlots:",err);
    return [];
  }
};

// ── PREMIUM CSS ─────────────────────────────────────────────────────────────────
const css = `
  @import url('https://fonts.cdnfonts.com/css/satoshi');
  @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,300;0,9..144,500;0,9..144,700;1,9..144,400&display=swap');

  :root {
    --bg: #F9F4F0;
    --card: #FFFFFF;
    --brand: #5A6993;
    --brand-deep: #323B52;
    --brand-light: #A1B0D8;
    --brand-wash: rgba(90,105,147,.07);
    --brand-wash-md: rgba(90,105,147,.12);
    --sage: #7B8F8A;
    --sage-wash: rgba(123,143,138,.09);
    --peach: #E6A184;
    --peach-light: #FFD8C2;
    --peach-wash: rgba(230,161,132,.10);
    --ink: #2D364D;
    --ink-soft: #4A5568;
    --muted: #9C8474;
    --sand: #D6D2C4;
    --sand-light: #EBE7DF;
    --cloud: #F4F7F9;
    --danger: #C0392B;
    --danger-wash: rgba(192,57,43,.08);
    --success: #27735A;
    --success-wash: rgba(39,115,90,.08);
    --font: 'Satoshi', 'Plus Jakarta Sans', sans-serif;
    --font-display: 'Fraunces', Georgia, serif;
    --r: 16px;
    --r-md: 12px;
    --r-lg: 20px;
    --r-xl: 28px;
    --r-full: 9999px;
    --ease: .22s cubic-bezier(.4,0,.2,1);
    --ease-spring: .38s cubic-bezier(.22,.68,0,1.1);
    --sh-sm: 0 1px 4px rgba(45,54,77,.05), 0 2px 10px rgba(45,54,77,.05);
    --sh-md: 0 2px 8px rgba(45,54,77,.06), 0 8px 28px rgba(45,54,77,.08);
    --sh-lg: 0 4px 16px rgba(45,54,77,.06), 0 20px 56px rgba(45,54,77,.10);
    --sh-card: 0 2px 8px rgba(45,54,77,.05), 0 12px 36px rgba(45,54,77,.07);
  }

  .mp-dark {
    --bg: #131720;
    --card: #1C2030;
    --brand: #8ea4c8;
    --brand-deep: #a1b0d8;
    --brand-light: #6b7ea8;
    --brand-wash: rgba(142,164,200,.10);
    --brand-wash-md: rgba(142,164,200,.16);
    --sage: #8faaa3;
    --sage-wash: rgba(143,170,163,.10);
    --peach: #e8b09a;
    --peach-wash: rgba(232,176,154,.10);
    --ink: #E0DDD8;
    --ink-soft: #B8B2AA;
    --muted: #9a9088;
    --sand: #4a4640;
    --sand-light: #2e2c28;
    --cloud: #181C28;
    --danger: #f87171;
    --danger-wash: rgba(248,113,113,.10);
    --success: #6ee7b7;
    --success-wash: rgba(110,231,183,.08);
    --sh-sm: 0 1px 4px rgba(0,0,0,.18), 0 2px 10px rgba(0,0,0,.14);
    --sh-md: 0 2px 8px rgba(0,0,0,.20), 0 8px 28px rgba(0,0,0,.22);
    --sh-lg: 0 4px 16px rgba(0,0,0,.20), 0 20px 56px rgba(0,0,0,.26);
    --sh-card: 0 2px 8px rgba(0,0,0,.18), 0 12px 36px rgba(0,0,0,.22);
  }

  .mp-root *, .mp-root *::before, .mp-root *::after { box-sizing: border-box; margin: 0; padding: 0; }
  .mp-root {
    font-family: var(--font);
    min-height: 100vh;
    background: var(--bg);
    color: var(--ink);
    -webkit-font-smoothing: antialiased;
    -webkit-tap-highlight-color: transparent;
    transition: background .3s ease, color .3s ease;
  }

  /* ── HEADER ── */
  .mp-hdr {
    background: linear-gradient(160deg, var(--brand-deep) 0%, var(--brand) 55%, #6d7faa 100%);
    padding: 0;
    position: relative;
    overflow: hidden;
  }
  .mp-hdr::before {
    content: '';
    position: absolute; inset: 0;
    background: radial-gradient(ellipse at 75% 30%, rgba(255,255,255,.09), transparent 60%),
                radial-gradient(ellipse at 15% 80%, rgba(230,161,132,.15), transparent 55%);
    pointer-events: none;
  }
  .mp-hdr-inner {
    position: relative; z-index: 1;
    max-width: 1100px; margin: 0 auto;
    padding: calc(env(safe-area-inset-top, 0px) + 16px) 20px 0;
  }
  .mp-hdr-top {
    display: flex; align-items: center; justify-content: space-between; gap: 10px;
    flex-wrap: wrap;
  }
  .mp-hdr-back {
    display: flex; align-items: center; gap: 6px;
    padding: 8px 16px;
    background: rgba(255,255,255,.14);
    border: 1px solid rgba(255,255,255,.22);
    border-radius: var(--r-full);
    color: #fff; font-family: var(--font); font-size: .82rem; font-weight: 600;
    cursor: pointer; text-decoration: none;
    transition: background var(--ease), transform var(--ease);
    white-space: nowrap;
  }
  .mp-hdr-back:hover { background: rgba(255,255,255,.22); transform: translateX(-2px); }
  .mp-hdr-back:active { transform: scale(.95); }
  .mp-hdr-actions { display: flex; gap: 8px; align-items: center; }
  .mp-hdr-btn {
    display: flex; align-items: center; gap: 6px;
    padding: 8px 16px;
    background: rgba(255,255,255,.14);
    border: 1px solid rgba(255,255,255,.22);
    border-radius: var(--r-full);
    color: #fff; font-family: var(--font); font-size: .82rem; font-weight: 600;
    cursor: pointer; white-space: nowrap; position: relative;
    transition: background var(--ease);
  }
  .mp-hdr-btn:hover { background: rgba(255,255,255,.22); }
  .mp-hdr-btn:active { transform: scale(.94); }
  .mp-hdr-hero {
    text-align: center;
    padding: 36px 20px 64px;
    color: #ffffff !important;
  }
  .mp-hdr-eyebrow {
    display: inline-flex; align-items: center; gap: 7px;
    font-size: .72rem; font-weight: 700; letter-spacing: .1em; text-transform: uppercase;
    color: var(--peach-light); margin-bottom: 14px;
    padding: 5px 14px;
    background: rgba(255,255,255,.1);
    border-radius: var(--r-full);
    border: 1px solid rgba(255,255,255,.15);
  }
  .mp-hdr-hero h1 {
    font-family: var(--font-display);
    font-size: clamp(1.8rem, 5vw, 2.8rem);
    font-weight: 700;
    line-height: 1.15;
    letter-spacing: -.02em;
    margin-bottom: 10px;
  }
  .mp-hdr-hero p {
    font-size: .88rem; opacity: .78; max-width: 400px; margin: 0 auto;
    line-height: 1.65; font-weight: 400;
    color: #ffffff !important
  }
  .mp-ct-badge {
    position: absolute; top: -5px; right: -5px;
    min-width: 17px; height: 17px;
    background: var(--peach);
    border: 2px solid rgba(90,105,147,.8);
    border-radius: var(--r-full);
    font-size: .58rem; font-weight: 800; color: #fff;
    display: flex; align-items: center; justify-content: center;
    padding: 0 3px;
  }

  /* ── LIVE BANNER ── */
  .mp-live-banner {
    background: linear-gradient(135deg, #C0392B, #922B21);
    padding: 13px 20px;
    display: flex; align-items: center; justify-content: space-between; gap: 12px;
    flex-wrap: wrap;
  }
  .mp-live-banner-text {
    display: flex; align-items: center; gap: 8px;
    color: #fff; font-weight: 700; font-size: .86rem;
  }
  .mp-live-dot {
    width: 8px; height: 8px; border-radius: 50%; background: #FFD8C2;
    animation: mpPulse 1.2s ease-in-out infinite;
    flex-shrink: 0;
  }
  @keyframes mpPulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.5;transform:scale(.85)} }
  .mp-live-join-btn {
    padding: 7px 18px;
    background: rgba(255,255,255,.95);
    color: #C0392B;
    border: none; border-radius: var(--r-full);
    font-family: var(--font); font-weight: 700; font-size: .8rem;
    cursor: pointer; transition: transform var(--ease);
  }
  .mp-live-join-btn:active { transform: scale(.94); }

  /* ── FILTERS ── */
  .mp-filters-wrap {
    max-width: 1100px; margin: -36px auto 0;
    padding: 0 16px;
    position: relative; z-index: 10;
  }
  .mp-filters-card {
    background: var(--card);
    border-radius: var(--r-xl);
    box-shadow: var(--sh-lg);
    border: 1px solid var(--sand-light);
    padding: 20px;
    transition: background .3s ease;
  }
  .mp-search-wrap { position: relative; }
  .mp-search-icon {
    position: absolute; left: 14px; top: 50%;
    transform: translateY(-50%);
    color: var(--muted); font-size: .95rem; pointer-events: none;
  }
  .mp-search {
    width: 100%; padding: 12px 14px 12px 40px;
    font-family: var(--font); font-size: .88rem; font-weight: 500;
    background: var(--cloud); color: var(--ink);
    border: 1.5px solid var(--sand-light);
    border-radius: var(--r-md);
    outline: none;
    transition: border-color var(--ease), box-shadow var(--ease), background var(--ease);
  }
  .mp-search::placeholder { color: var(--muted); font-weight: 400; }
  .mp-search:focus {
    border-color: var(--brand-light);
    background: var(--card);
    box-shadow: 0 0 0 4px var(--brand-wash);
  }
  .mp-filter-row {
    display: flex; gap: 8px; margin-top: 12px; flex-wrap: wrap;
  }
  .mp-select {
    flex: 1; min-width: 130px;
    padding: 10px 14px;
    font-family: var(--font); font-size: .84rem; font-weight: 600;
    background: var(--cloud); color: var(--ink);
    border: 1.5px solid var(--sand-light);
    border-radius: var(--r-md);
    outline: none; cursor: pointer;
    transition: border-color var(--ease), background var(--ease);
    appearance: none;
    -webkit-appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%239C8474' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 12px center;
    padding-right: 32px;
  }
  .mp-select:focus { border-color: var(--brand-light); background-color: var(--card); box-shadow: 0 0 0 4px var(--brand-wash); }

  /* ── CATEGORY CHIPS ── */
  .mp-cat-scroll {
    display: flex; gap: 7px; margin-top: 12px;
    overflow-x: auto; scrollbar-width: none;
    -webkit-overflow-scrolling: touch;
    padding-bottom: 2px;
  }
  .mp-cat-scroll::-webkit-scrollbar { display: none; }
  .mp-cat-chip {
    flex-shrink: 0;
    padding: 7px 14px;
    border-radius: var(--r-full);
    font-family: var(--font); font-size: .78rem; font-weight: 700;
    cursor: pointer; white-space: nowrap;
    border: 1.5px solid var(--sand-light);
    background: var(--cloud); color: var(--muted);
    transition: all var(--ease);
  }
  .mp-cat-chip:active { transform: scale(.93); }
  .mp-cat-chip.on {
    background: var(--brand); color: #fff;
    border-color: var(--brand);
    box-shadow: 0 4px 14px var(--brand-wash-md);
  }

  /* ── CONTENT ── */
  .mp-content {
    max-width: 1100px; margin: 36px auto 80px;
    padding: 0 16px;
  }
  .mp-result-line {
    font-size: .8rem; color: var(--muted); font-weight: 600;
    margin-bottom: 18px;
    letter-spacing: .02em;
  }

  /* ── MENTOR GRID ── */
  .mp-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 14px;
  }
  @media(min-width: 520px) { .mp-grid { grid-template-columns: 1fr 1fr; } }
  @media(min-width: 860px) { .mp-grid { grid-template-columns: 1fr 1fr 1fr; } }

  /* ── MENTOR CARD ── */
  .mp-card {
    background: var(--card);
    border: 1px solid var(--sand-light);
    border-radius: var(--r-xl);
    box-shadow: var(--sh-card);
    overflow: hidden;
    cursor: pointer;
    transition: transform .28s var(--ease-spring), box-shadow .28s ease, border-color .2s ease;
    animation: mpCardIn .45s var(--ease-spring) both;
  }
  .mp-card:hover {
    transform: translateY(-5px);
    box-shadow: var(--sh-lg);
    border-color: var(--brand-light);
  }
  .mp-card:active { transform: translateY(-2px) scale(.99); }
  @keyframes mpCardIn {
    from { opacity: 0; transform: translateY(18px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .mp-card-photo-wrap {
    position: relative;
    background: linear-gradient(160deg, var(--brand-wash) 0%, var(--peach-wash) 100%);
    padding: 24px 20px 0;
    display: flex; justify-content: center;
    min-height: 96px;
  }
  .mp-avatar {
    width: 78px; height: 78px; border-radius: 50%;
    object-fit: cover;
    border: 3px solid var(--card);
    box-shadow: 0 4px 16px rgba(45,54,77,.14);
  }
  .mp-avatar-ph {
    width: 78px; height: 78px; border-radius: 50%;
    background: linear-gradient(135deg, var(--brand-wash-md), var(--peach-wash));
    border: 3px solid var(--card);
    display: flex; align-items: center; justify-content: center;
    font-size: 2rem;
    box-shadow: 0 4px 16px rgba(45,54,77,.10);
  }
  .mp-card-badge {
    position: absolute; top: 12px; right: 12px;
    padding: 3px 9px;
    border-radius: var(--r-full);
    font-size: .62rem; font-weight: 800; letter-spacing: .05em;
    text-transform: uppercase;
    background: var(--brand); color: #fff;
    box-shadow: 0 2px 8px var(--brand-wash-md);
  }
  .mp-li-badge {
    position: absolute; top: 12px; left: 12px;
    padding: 3px 9px;
    border-radius: var(--r-full);
    font-size: .62rem; font-weight: 800;
    background: #E8F0F8; color: #0077b5;
    border: 1px solid rgba(0,119,181,.15);
  }
  .mp-dark .mp-li-badge { background: rgba(0,119,181,.15); color: #5aafe0; }
  .mp-card-body {
    padding: 14px 18px 18px;
    text-align: center;
  }
  .mp-name {
    font-family: var(--font-display);
    font-size: 1.05rem; font-weight: 700;
    color: var(--ink); margin-bottom: 3px;
    letter-spacing: -.01em;
  }
  .mp-role { font-size: .78rem; color: var(--muted); margin-bottom: 9px; font-weight: 500; }
  .mp-rating {
    display: flex; align-items: center; justify-content: center; gap: 5px;
    margin-bottom: 10px;
  }
  .mp-stars { color: #E6A184; font-size: .88rem; letter-spacing: -.5px; }
  .mp-rval { font-weight: 800; font-size: .84rem; color: var(--ink); }
  .mp-rct { font-size: .74rem; color: var(--muted); }
  .mp-tags {
    display: flex; flex-wrap: wrap; justify-content: center; gap: 5px;
    margin-bottom: 12px;
  }
  .mp-tag {
    padding: 3px 10px;
    background: var(--brand-wash);
    border-radius: var(--r-full);
    font-size: .68rem; font-weight: 700; color: var(--brand);
    border: 1px solid rgba(90,105,147,.12);
  }
  .mp-price {
    font-family: var(--font-display);
    font-size: 1.15rem; font-weight: 700; color: var(--ink);
    margin-bottom: 14px;
  }
  .mp-price span { font-size: .74rem; color: var(--muted); font-weight: 400; font-family: var(--font); }
  .mp-book-btn {
    width: 100%; padding: 11px;
    background: linear-gradient(135deg, var(--brand), var(--brand-deep));
    color: #fff; border: none; border-radius: var(--r-md);
    font-family: var(--font); font-weight: 700; font-size: .86rem;
    cursor: pointer;
    box-shadow: 0 4px 14px var(--brand-wash-md);
    transition: all var(--ease);
  }
  .mp-book-btn:hover { transform: translateY(-1px); box-shadow: 0 6px 20px var(--brand-wash-md); }
  .mp-book-btn:active { transform: scale(.97); }

  /* ── SKELETON ── */
  .mp-skel {
    background: var(--card);
    border-radius: var(--r-xl);
    border: 1px solid var(--sand-light);
    overflow: hidden;
  }
  .mp-skel-top { height: 110px; }
  .mp-skel-body { padding: 16px; }
  .mp-skel-ln { height: 10px; border-radius: 6px; margin-bottom: 9px; }
  .mp-shimmer {
    background: linear-gradient(90deg, var(--cloud) 25%, var(--sand-light) 50%, var(--cloud) 75%);
    background-size: 200% 100%;
    animation: mpShimmer 1.6s ease-in-out infinite;
  }
  @keyframes mpShimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }

  /* ── EMPTY ── */
  .mp-empty {
    text-align: center; padding: 60px 24px; color: var(--muted);
    grid-column: 1 / -1;
  }
  .mp-empty-icon { font-size: 3rem; display: block; margin-bottom: 14px; }

  /* ── OVERLAY / MODAL ── */
  .mp-overlay {
    position: fixed; inset: 0;
    background: rgba(20,24,38,.48);
    backdrop-filter: blur(6px);
    -webkit-backdrop-filter: blur(6px);
    z-index: 1000;
    display: flex; align-items: flex-end; justify-content: center;
    animation: mpFadeIn .22s ease;
  }
  @media(min-width: 600px) {
    .mp-overlay { align-items: center; padding: 20px; }
  }
  @keyframes mpFadeIn { from{opacity:0} to{opacity:1} }

  /* Bottom-sheet style on mobile, centered card on desktop */
  .mp-modal {
    background: var(--card);
    border-radius: var(--r-xl) var(--r-xl) 0 0;
    width: 100%; max-width: 560px;
    max-height: 94dvh;
    overflow-y: auto; overflow-x: hidden;
    display: flex; flex-direction: column;
    box-shadow: var(--sh-lg);
    animation: mpSheetUp .35s var(--ease-spring);
    border: 1px solid var(--sand-light);
    border-bottom: none;
    transition: background .3s ease;
  }
  @media(min-width: 600px) {
    .mp-modal {
      border-radius: var(--r-xl);
      border-bottom: 1px solid var(--sand-light);
      animation: mpScaleIn .3s var(--ease-spring);
    }
  }
  @keyframes mpSheetUp { from{transform:translateY(100%)} to{transform:translateY(0)} }
  @keyframes mpScaleIn { from{opacity:0;transform:scale(.96) translateY(10px)} to{opacity:1;transform:scale(1) translateY(0)} }

  .mp-modal-handle {
    width: 40px; height: 4px; border-radius: 2px;
    background: var(--sand-light);
    margin: 10px auto 0;
    flex-shrink: 0;
  }
  @media(min-width: 600px) { .mp-modal-handle { display: none; } }

  .mp-modal-hdr {
    padding: 16px 20px 0;
    display: flex; align-items: center; justify-content: space-between;
    flex-shrink: 0;
  }
  .mp-modal-title {
    font-family: var(--font-display);
    font-size: 1.1rem; font-weight: 700; color: var(--ink);
    letter-spacing: -.01em;
  }
  .mp-modal-close {
    width: 34px; height: 34px;
    border: 1.5px solid var(--sand-light);
    background: var(--cloud); color: var(--muted);
    border-radius: 50%; cursor: pointer; font-size: .95rem;
    display: flex; align-items: center; justify-content: center;
    transition: all var(--ease);
  }
  .mp-modal-close:hover { background: var(--sand-light); color: var(--ink); }
  .mp-modal-body { padding: 16px 20px 32px; overflow-y: auto; }

  /* ── FORM ELEMENTS ── */
  .mp-fg { display: flex; flex-direction: column; gap: 5px; margin-bottom: 13px; }
  .mp-fg label {
    font-size: .66rem; font-weight: 800; color: var(--muted);
    text-transform: uppercase; letter-spacing: .1em;
  }
  .mp-fg input, .mp-fg select, .mp-fg textarea {
    padding: 11px 14px;
    border: 1.5px solid var(--sand-light);
    border-radius: var(--r-md);
    font-family: var(--font); font-size: .88rem; font-weight: 500;
    background: var(--cloud); color: var(--ink);
    outline: none;
    transition: border-color var(--ease), box-shadow var(--ease), background var(--ease);
  }
  .mp-fg input:focus, .mp-fg select:focus, .mp-fg textarea:focus {
    border-color: var(--brand-light);
    background: var(--card);
    box-shadow: 0 0 0 4px var(--brand-wash);
  }
  .mp-fg-row { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }

  /* ── SLOT GRID ── */
  .mp-slot-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(78px, 1fr));
    gap: 7px;
  }
  .mp-slot {
    padding: 9px 6px;
    border: 1.5px solid var(--sand-light);
    border-radius: var(--r-md);
    font-size: .78rem; font-weight: 700; text-align: center;
    cursor: pointer; background: var(--cloud); color: var(--ink-soft);
    transition: all .18s var(--ease-spring);
  }
  .mp-slot:hover { border-color: var(--brand-light); background: var(--brand-wash); color: var(--brand); }
  .mp-slot:active { transform: scale(.94); }
  .mp-slot.selected {
    background: linear-gradient(135deg, var(--brand), var(--brand-deep));
    color: #fff; border-color: transparent;
    box-shadow: 0 4px 12px var(--brand-wash-md);
    transform: translateY(-1px);
  }

  /* ── PRICE BOX ── */
  .mp-price-box {
    background: var(--brand-wash);
    border: 1px solid rgba(90,105,147,.12);
    border-radius: var(--r-lg); padding: 15px 16px; margin-bottom: 15px;
  }
  .mp-price-row {
    display: flex; justify-content: space-between; align-items: center;
    font-size: .84rem; padding: 5px 0;
    border-bottom: 1px solid rgba(90,105,147,.08); color: var(--ink-soft);
  }
  .mp-price-row:last-child {
    border: none; font-weight: 800; font-size: .95rem;
    color: var(--ink); padding-top: 8px; margin-top: 2px;
  }

  /* ── ALERTS ── */
  .mp-alert {
    padding: 11px 14px;
    border-radius: var(--r-md);
    font-size: .82rem; font-weight: 500; line-height: 1.55;
    margin-bottom: 12px;
    display: flex; align-items: flex-start; gap: 8px;
    animation: mpAlertIn .25s ease;
  }
  @keyframes mpAlertIn { from{opacity:0;transform:translateY(5px)} to{opacity:1;transform:translateY(0)} }
  .mp-alert.error { background: var(--danger-wash); color: var(--danger); border: 1px solid rgba(192,57,43,.18); }
  .mp-alert.info  { background: var(--brand-wash); color: var(--brand); border: 1px solid rgba(90,105,147,.15); }
  .mp-alert.success { background: var(--success-wash); color: var(--success); border: 1px solid rgba(39,115,90,.18); }
  .mp-alert.warning { background: var(--peach-wash); color: #8B4513; border: 1px solid rgba(230,161,132,.25); }
  .mp-dark .mp-alert.warning { color: var(--peach); }

  /* ── BUTTONS ── */
  .mp-btn-primary {
    width: 100%; padding: 14px;
    background: linear-gradient(135deg, var(--brand), var(--brand-deep));
    color: #fff; border: none; border-radius: var(--r-md);
    font-family: var(--font); font-weight: 700; font-size: .9rem;
    cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 8px;
    box-shadow: 0 4px 16px var(--brand-wash-md);
    transition: all var(--ease);
    letter-spacing: .01em;
  }
  .mp-btn-primary:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 6px 22px var(--brand-wash-md); }
  .mp-btn-primary:active:not(:disabled) { transform: scale(.97); }
  .mp-btn-primary:disabled { opacity: .45; cursor: not-allowed; transform: none; }

  .mp-btn-ghost {
    width: 100%; padding: 12px;
    background: none; color: var(--muted);
    border: 1.5px solid var(--sand-light);
    border-radius: var(--r-md);
    font-family: var(--font); font-weight: 600; font-size: .86rem;
    cursor: pointer;
    transition: all var(--ease);
  }
  .mp-btn-ghost:hover { border-color: var(--sand); color: var(--ink); background: var(--cloud); }
  .mp-btn-ghost:active { transform: scale(.97); }

  .mp-btn-danger {
    padding: 10px 18px;
    background: var(--danger-wash);
    color: var(--danger);
    border: 1.5px solid rgba(192,57,43,.2);
    border-radius: var(--r-md);
    font-family: var(--font); font-weight: 700; font-size: .84rem;
    cursor: pointer; transition: all var(--ease);
  }
  .mp-btn-danger:hover { background: rgba(192,57,43,.14); }
  .mp-btn-danger:active { transform: scale(.95); }
  .mp-btn-danger.solid {
    background: var(--danger); color: #fff;
    box-shadow: 0 4px 14px rgba(192,57,43,.25);
    border-color: transparent;
  }

  .mp-spin {
    width: 15px; height: 15px; flex-shrink: 0;
    border: 2px solid rgba(255,255,255,.3); border-top-color: #fff;
    border-radius: 50%;
    animation: mpSpin .6s linear infinite; display: inline-block;
  }
  @keyframes mpSpin { to{transform:rotate(360deg)} }

  /* ── SECURITY NOTE ── */
  .mp-security {
    display: flex; align-items: flex-start; gap: 9px;
    padding: 10px 13px;
    background: var(--brand-wash);
    border: 1px solid rgba(90,105,147,.12);
    border-radius: var(--r-md);
    font-size: .76rem; color: var(--brand); line-height: 1.55;
    margin-bottom: 14px; font-weight: 500;
  }

  /* ── MENTOR PREVIEW (in booking modal) ── */
  .mp-mentor-preview {
    display: flex; gap: 13px; align-items: center;
    padding: 13px 15px;
    background: var(--cloud);
    border-radius: var(--r-lg);
    border: 1px solid var(--sand-light);
    margin-bottom: 16px;
  }
  .mp-preview-av { width: 52px; height: 52px; border-radius: 50%; object-fit: cover; flex-shrink: 0; }
  .mp-preview-av-ph {
    width: 52px; height: 52px; border-radius: 50%;
    background: linear-gradient(135deg, var(--brand-wash-md), var(--peach-wash));
    display: flex; align-items: center; justify-content: center; font-size: 1.4rem; flex-shrink: 0;
  }

  /* ── SESSIONS MODAL ── */
  .mp-sessions-modal {
    background: var(--card);
    border-radius: var(--r-xl) var(--r-xl) 0 0;
    width: 100%; max-width: 640px;
    max-height: 94dvh;
    display: flex; flex-direction: column;
    box-shadow: var(--sh-lg);
    animation: mpSheetUp .35s var(--ease-spring);
    border: 1px solid var(--sand-light); border-bottom: none;
  }
  @media(min-width: 600px) {
    .mp-sessions-modal {
      border-radius: var(--r-xl); border-bottom: 1px solid var(--sand-light);
      animation: mpScaleIn .3s var(--ease-spring);
    }
  }
  .mp-sessions-hdr {
    padding: 16px 20px 12px;
    border-bottom: 1px solid var(--sand-light);
    display: flex; justify-content: space-between; align-items: flex-start;
    flex-shrink: 0;
  }
  .mp-stabs {
    display: flex; border-bottom: 1px solid var(--sand-light);
    flex-shrink: 0; overflow-x: auto; scrollbar-width: none;
  }
  .mp-stabs::-webkit-scrollbar { display: none; }
  .mp-stab {
    flex: 1; padding: 12px 8px;
    border: none; background: none;
    font-family: var(--font); font-size: .78rem; font-weight: 700;
    color: var(--muted); cursor: pointer;
    border-bottom: 2.5px solid transparent;
    white-space: nowrap; min-width: 80px;
    transition: color var(--ease), border-color var(--ease);
  }
  .mp-stab.active { color: var(--brand); border-bottom-color: var(--brand); }
  .mp-sbody { flex: 1; overflow-y: auto; padding: 14px; }

  /* ── APPOINTMENT CARDS ── */
  .mp-apt {
    border: 1.5px solid var(--sand-light);
    border-radius: var(--r-lg);
    padding: 15px 15px 12px;
    margin-bottom: 10px;
    background: var(--card);
    transition: border-color var(--ease);
  }
  .mp-apt.live { border-color: var(--danger); background: var(--danger-wash); }
  .mp-apt.done { background: var(--success-wash); border-color: rgba(39,115,90,.18); }
  .mp-apt.xed  { opacity: .6; }
  .mp-apt-top {
    display: flex; justify-content: space-between; align-items: flex-start;
    margin-bottom: 7px; gap: 8px;
  }
  .mp-apt-name {
    font-family: var(--font-display); font-weight: 700; font-size: .95rem; color: var(--ink);
    letter-spacing: -.01em;
  }
  .mp-apt-badge {
    padding: 3px 10px; border-radius: var(--r-full);
    font-size: .66rem; font-weight: 800; white-space: nowrap;
    text-transform: uppercase; letter-spacing: .04em;
  }
  .mp-apt-badge.live { background: var(--danger); color: #fff; animation: mpBlink 1.2s infinite; }
  @keyframes mpBlink { 0%,100%{opacity:1}50%{opacity:.6} }
  .mp-apt-badge.upcoming { background: var(--brand-wash); color: var(--brand); }
  .mp-apt-badge.done { background: var(--success-wash); color: var(--success); }
  .mp-apt-badge.xed { background: var(--cloud); color: var(--muted); }
  .mp-apt-info { font-size: .78rem; color: var(--muted); margin-bottom: 3px; font-weight: 500; }
  .mp-apt-topic { font-size: .84rem; color: var(--ink-soft); margin-bottom: 9px; font-weight: 500; }

  .mp-receipt {
    background: var(--cloud);
    border-radius: var(--r-md);
    padding: 9px 13px; margin-bottom: 9px;
    border: 1px solid var(--sand-light);
  }
  .mp-receipt-row {
    display: flex; justify-content: space-between;
    padding: 3px 0; font-size: .78rem; color: var(--muted);
  }
  .mp-receipt-row.total {
    font-weight: 800; color: var(--ink); font-size: .84rem;
    border-top: 1px solid var(--sand-light); margin-top: 4px; padding-top: 5px;
  }
  .mp-apt-actions { display: flex; gap: 7px; flex-wrap: wrap; margin-top: 7px; }

  /* Action buttons */
  .mp-join {
    padding: 9px 16px;
    background: var(--danger); color: #fff; border: none;
    border-radius: var(--r-md);
    font-family: var(--font); font-weight: 700; font-size: .8rem;
    cursor: pointer;
    animation: mpJoinPulse 1.6s infinite;
  }
  @keyframes mpJoinPulse {
    0%,100%{box-shadow:0 0 0 0 rgba(192,57,43,.4)}
    50%{box-shadow:0 0 0 8px rgba(192,57,43,0)}
  }
  .mp-confirm-btn {
    padding: 9px 16px;
    background: var(--success-wash);
    color: var(--success); border: 1.5px solid rgba(39,115,90,.2);
    border-radius: var(--r-md);
    font-family: var(--font); font-weight: 700; font-size: .8rem; cursor: pointer;
    transition: all var(--ease);
  }
  .mp-confirm-btn:hover { background: rgba(39,115,90,.14); }
  .mp-confirm-btn:disabled { opacity: .5; }
  .mp-cancel-btn {
    padding: 8px 13px;
    background: var(--danger-wash); color: var(--danger);
    border: 1.5px solid rgba(192,57,43,.18);
    border-radius: var(--r-md);
    font-family: var(--font); font-size: .78rem; font-weight: 700; cursor: pointer;
    transition: all var(--ease);
  }
  .mp-cancel-btn:hover { background: rgba(192,57,43,.12); }
  .mp-fb-btn {
    padding: 8px 13px;
    background: var(--brand-wash); color: var(--brand);
    border: 1.5px solid rgba(90,105,147,.15);
    border-radius: var(--r-md);
    font-family: var(--font); font-size: .78rem; font-weight: 700; cursor: pointer;
    transition: all var(--ease);
  }
  .mp-fb-btn:hover { background: var(--brand-wash-md); }
  .mp-rpt-btn {
    padding: 8px 13px;
    background: var(--peach-wash); color: #8B4513;
    border: 1.5px solid rgba(230,161,132,.2);
    border-radius: var(--r-md);
    font-family: var(--font); font-size: .78rem; font-weight: 700; cursor: pointer;
  }
  .mp-dark .mp-rpt-btn { color: var(--peach); }
  .mp-join-soon {
    padding: 9px 16px;
    background: var(--success-wash); color: var(--success);
    border: 2px solid rgba(39,115,90,.3);
    border-radius: var(--r-md);
    font-family: var(--font); font-weight: 700; font-size: .8rem;
    cursor: pointer; display: flex; align-items: center; gap: 6px;
    animation: mpJoinPulseGreen 1.6s infinite;
  }
  @keyframes mpJoinPulseGreen {
    0%,100%{box-shadow:0 0 0 0 rgba(39,115,90,.4)}
    50%{box-shadow:0 0 0 8px rgba(39,115,90,0)}
  }
  .mp-join-video-soon {
    padding: 9px 16px;
    background: linear-gradient(135deg, var(--brand), var(--brand-deep));
    color: #fff; border: none; border-radius: var(--r-md);
    font-family: var(--font); font-weight: 700; font-size: .8rem; cursor: pointer;
    animation: mpJoinPulse 1.6s infinite;
  }
  .mp-join-locked {
    padding: 9px 16px;
    background: var(--cloud); color: var(--muted);
    border: 1.5px solid var(--sand-light);
    border-radius: var(--r-md);
    font-size: .8rem; font-weight: 600; cursor: not-allowed;
    display: flex; align-items: center; gap: 6px;
  }

  /* pills / countdown */
  .mp-countdown-pill {
    display: inline-flex; align-items: center; gap: 5px;
    padding: 5px 11px;
    background: var(--brand-wash); border-radius: var(--r-full);
    font-size: .74rem; font-weight: 700; color: var(--brand); margin-bottom: 8px;
    border: 1px solid rgba(90,105,147,.12);
  }
  .mp-soon-pill {
    display: inline-flex; align-items: center; gap: 5px;
    padding: 5px 11px;
    background: var(--success-wash); border-radius: var(--r-full);
    font-size: .74rem; font-weight: 700; color: var(--success); margin-bottom: 8px;
    border: 1px solid rgba(39,115,90,.18);
    animation: mpBlink 1s infinite;
  }

  /* Guide */
  .mp-guide-toggle {
    display: flex; align-items: center; gap: 5px;
    background: none; border: none; cursor: pointer;
    font-family: var(--font); font-size: .76rem; font-weight: 700;
    color: var(--brand); padding: 4px 0; margin-top: 4px;
    transition: color var(--ease);
  }
  .mp-guide-body {
    background: var(--cloud); border-radius: var(--r-md);
    padding: 12px 14px; margin-top: 8px; border: 1px solid var(--sand-light);
  }
  .mp-guide-step {
    display: flex; align-items: flex-start; gap: 10px;
    padding: 6px 0; font-size: .8rem; color: var(--ink-soft);
    border-bottom: 1px solid var(--sand-light);
  }
  .mp-guide-step:last-child { border: none; }
  .mp-guide-num {
    width: 22px; height: 22px; border-radius: 50%;
    background: linear-gradient(135deg, var(--brand), var(--brand-deep));
    color: #fff; font-size: .66rem; font-weight: 800;
    display: flex; align-items: center; justify-content: center; flex-shrink: 0; margin-top: 1px;
  }

  /* Cancel section */
  .mp-cancel-section { margin-top: 10px; padding-top: 10px; border-top: 1px solid var(--sand-light); }
  .mp-cancel-preview {
    display: flex; justify-content: space-between; font-size: .78rem; margin-bottom: 8px; color: var(--muted);
  }
  .mp-cancel-preview .refund { font-weight: 800; color: var(--success); }
  .mp-cancel-preview .fee { color: var(--danger); }
  .mp-empty-state { text-align: center; padding: 40px 20px; color: var(--muted); }
  .mp-empty-state-icon { font-size: 2.5rem; margin-bottom: 10px; display: block; }

  /* ── CHAT ── */
  .mp-chat-modal {
    background: var(--card);
    border-radius: var(--r-xl) var(--r-xl) 0 0;
    width: 100%; max-width: 600px;
    height: 92dvh;
    display: flex; flex-direction: column;
    box-shadow: var(--sh-lg);
    animation: mpSheetUp .35s var(--ease-spring);
    border: 1px solid var(--sand-light); border-bottom: none;
  }
  @media(min-width: 600px) {
    .mp-chat-modal {
      border-radius: var(--r-xl); border-bottom: 1px solid var(--sand-light);
      height: 82vh;
      animation: mpScaleIn .3s var(--ease-spring);
    }
  }
  .mp-chat-hdr {
    padding: 14px 18px;
    border-bottom: 1px solid var(--sand-light);
    display: flex; align-items: center; justify-content: space-between;
    flex-shrink: 0;
  }
  .mp-chat-timer {
    padding: 4px 11px;
    background: var(--brand-wash); border-radius: var(--r-full);
    font-size: .76rem; font-weight: 800; color: var(--brand);
    border: 1px solid rgba(90,105,147,.12);
  }
  .mp-chat-timer.warn { background: var(--danger-wash); color: var(--danger); animation: mpBlink 1s infinite; border-color: rgba(192,57,43,.2); }
  .mp-ctabs { display: flex; border-bottom: 1px solid var(--sand-light); flex-shrink: 0; }
  .mp-ctab {
    flex: 1; padding: 11px;
    border: none; background: none;
    font-family: var(--font); font-size: .84rem; font-weight: 700;
    color: var(--muted); cursor: pointer;
    border-bottom: 2.5px solid transparent;
    transition: color var(--ease), border-color var(--ease);
  }
  .mp-ctab.active { color: var(--brand); border-bottom-color: var(--brand); }
  .mp-msgs {
    flex: 1; overflow-y: auto; padding: 14px;
    display: flex; flex-direction: column; gap: 9px;
    -webkit-overflow-scrolling: touch;
  }
  .mp-msg { max-width: 78%; }
  .mp-msg.sent { align-self: flex-end; }
  .mp-msg.recv { align-self: flex-start; }
  .mp-msg-bbl {
    padding: 10px 14px;
    border-radius: 18px; font-size: .88rem; line-height: 1.5;
    word-break: break-word;
  }
  .mp-msg.sent .mp-msg-bbl {
    background: linear-gradient(135deg, var(--brand), var(--brand-deep));
    color: #fff; border-radius: 18px 18px 4px 18px;
    box-shadow: 0 3px 10px var(--brand-wash-md);
  }
  .mp-msg.recv .mp-msg-bbl {
    background: var(--cloud); color: var(--ink);
    border: 1px solid var(--sand-light);
    border-radius: 4px 18px 18px 18px;
  }
  .mp-msg-time { font-size: .66rem; color: var(--muted); margin-top: 3px; font-weight: 500; }
  .mp-msg.sent .mp-msg-time { text-align: right; }
  .mp-input-row {
    padding: 11px 14px calc(11px + env(safe-area-inset-bottom, 0px));
    border-top: 1px solid var(--sand-light);
    display: flex; gap: 8px; flex-shrink: 0;
    background: var(--card);
  }
  .mp-input-row input {
    flex: 1; padding: 11px 15px;
    border: 1.5px solid var(--sand-light); border-radius: var(--r-full);
    font-size: .88rem; font-family: var(--font); outline: none;
    background: var(--cloud); color: var(--ink);
    transition: border-color var(--ease);
  }
  .mp-input-row input:focus { border-color: var(--brand-light); }
  .mp-send {
    padding: 10px 18px;
    background: linear-gradient(135deg, var(--brand), var(--brand-deep));
    color: #fff; border: none; border-radius: var(--r-full);
    font-family: var(--font); font-weight: 700; font-size: .86rem;
    cursor: pointer; flex-shrink: 0;
    box-shadow: 0 3px 10px var(--brand-wash-md);
    transition: all var(--ease);
  }
  .mp-send:hover { transform: translateY(-1px); }
  .mp-send:active { transform: scale(.94); }
  .mp-video-wrap { flex: 1; overflow: hidden; display: flex; flex-direction: column; }

  /* ── CONFIRM BOOKING ── */
  .mp-confirm-modal {
    background: var(--card);
    border-radius: var(--r-xl) var(--r-xl) 0 0;
    width: 100%; max-width: 520px;
    max-height: 94dvh; overflow-y: auto;
    animation: mpSheetUp .35s var(--ease-spring);
    box-shadow: var(--sh-lg);
    border: 1px solid var(--sand-light); border-bottom: none;
  }
  @media(min-width: 600px) {
    .mp-confirm-modal { border-radius: var(--r-xl); border-bottom: 1px solid var(--sand-light); animation: mpScaleIn .3s var(--ease-spring); }
  }
  .mp-confirm-hero {
    background: linear-gradient(160deg, var(--brand-deep), var(--brand) 60%, #6d7faa);
    padding: 32px 24px 28px; text-align: center; color: #fff;
    border-radius: var(--r-xl) var(--r-xl) 0 0;
    position: relative; overflow: hidden;
  }
  .mp-confirm-hero::before {
    content: '';
    position: absolute; inset: 0;
    background: radial-gradient(ellipse at 70% 30%, rgba(255,255,255,.1), transparent 60%);
  }
  .mp-confirm-icon {
    font-size: 2.8rem; display: block; margin-bottom: 10px; position: relative; z-index: 1;
    animation: mpConfirmBounce .5s cubic-bezier(.22,.68,0,1.55);
  }
  @keyframes mpConfirmBounce { from{transform:scale(.3);opacity:0}to{transform:scale(1);opacity:1} }
  .mp-confirm-body { padding: 20px; }
  .mp-confirm-section { margin-bottom: 16px; }
  .mp-confirm-section h4 {
    font-family: var(--font-display); font-size: .78rem; font-weight: 700;
    color: var(--brand); text-transform: uppercase; letter-spacing: .07em;
    margin-bottom: 9px; padding-bottom: 6px;
    border-bottom: 2px solid var(--sand-light);
  }
  .mp-confirm-row {
    display: flex; justify-content: space-between; align-items: center;
    padding: 5px 0; font-size: .875rem;
    border-bottom: 1px solid var(--sand-light);
  }
  .mp-confirm-row:last-child { border: none; }
  .mp-confirm-label { color: var(--muted); }
  .mp-confirm-val { font-weight: 700; color: var(--ink); text-align: right; }
  .mp-confirm-id {
    font-family: monospace; font-size: .74rem;
    background: var(--brand-wash);
    padding: 9px 13px; border-radius: var(--r-md);
    color: var(--brand); word-break: break-all; margin-bottom: 10px;
    border: 1px solid rgba(90,105,147,.12);
  }

  /* ── TOASTS ── */
  .mp-toasts {
    position: fixed; bottom: calc(24px + env(safe-area-inset-bottom, 0px));
    left: 50%; transform: translateX(-50%);
    z-index: 9999; display: flex; flex-direction: column;
    gap: 8px; align-items: center; pointer-events: none;
  }
  .mp-toast {
    padding: 12px 22px; border-radius: var(--r-md);
    font-family: var(--font); font-size: .86rem; font-weight: 700; color: #fff;
    box-shadow: var(--sh-lg);
    animation: mpToastIn .3s var(--ease-spring);
    max-width: calc(100vw - 32px); text-align: center; pointer-events: all;
  }
  @keyframes mpToastIn { from{opacity:0;transform:translateY(10px) scale(.95)}to{opacity:1;transform:translateY(0) scale(1)} }
  .mp-toast.success { background: linear-gradient(135deg, var(--sage), #5a7a75); }
  .mp-toast.error   { background: linear-gradient(135deg, var(--danger), #922B21); }
  .mp-toast.info    { background: linear-gradient(135deg, var(--brand), var(--brand-deep)); }

  /* ── NOTIF DROPDOWN ── */
  .mp-notif-drop {
    position: fixed; top: calc(env(safe-area-inset-top,0px) + 68px); right: 16px;
    width: min(300px, calc(100vw - 32px));
    background: var(--card); border-radius: var(--r-xl);
    box-shadow: var(--sh-lg); z-index: 600;
    max-height: 360px; overflow: auto;
    border: 1px solid var(--sand-light);
    animation: mpScaleIn .22s var(--ease-spring);
  }
  .mp-notif-hdr {
    padding: 13px 16px; border-bottom: 1px solid var(--sand-light);
    font-family: var(--font-display); font-weight: 700; font-size: .9rem; color: var(--ink);
    display: flex; justify-content: space-between; align-items: center;
  }
  .mp-notif-close {
    background: none; border: none; cursor: pointer;
    color: var(--muted); font-size: 1rem; padding: 0 2px;
  }
  .mp-notif-row {
    padding: 10px 14px; border-bottom: 1px solid var(--sand-light);
    transition: background var(--ease);
  }
  .mp-notif-row.unread { background: var(--brand-wash); border-left: 3px solid var(--brand); }
  .mp-notif-title { font-size: .8rem; font-weight: 700; color: var(--ink); }
  .mp-notif-msg { font-size: .76rem; color: var(--muted); margin-top: 2px; line-height: 1.4; }
  .mp-notif-date { font-size: .68rem; color: var(--sand); margin-top: 3px; }

  /* ── STARS rating ── */
  .mp-star-row { display: flex; gap: 10px; font-size: 2rem; margin: 4px 0 10px; }
  .mp-star-btn { background: none; border: none; cursor: pointer; transition: transform .15s var(--ease-spring); }
  .mp-star-btn:active { transform: scale(1.4); }

  /* ── REPORT OPTIONS ── */
  .mp-report-opts { display: flex; flex-direction: column; gap: 7px; margin-bottom: 14px; }
  .mp-report-opt {
    padding: 12px 15px; border-radius: var(--r-md); text-align: left;
    background: var(--cloud); color: var(--ink-soft);
    border: 1.5px solid var(--sand-light);
    font-family: var(--font); font-size: .86rem; font-weight: 600; cursor: pointer;
    transition: all var(--ease);
  }
  .mp-report-opt:active { transform: scale(.98); }
  .mp-report-opt.on { border-color: var(--brand); background: var(--brand-wash); color: var(--brand); }

  /* ── THEME TOGGLE ── */
  .mp-theme-btn {
    position: fixed; top: calc(env(safe-area-inset-top,0px) + 12px); right: 14px;
    z-index: 400; width: 36px; height: 36px;
    border-radius: 50%; border: 1.5px solid rgba(255,255,255,.3);
    background: rgba(255,255,255,.18); color: #fff;
    backdrop-filter: blur(10px); cursor: pointer; font-size: .88rem;
    display: flex; align-items: center; justify-content: center;
    transition: all var(--ease);
  }
  .mp-theme-btn:hover { background: rgba(255,255,255,.28); transform: scale(1.08); }
  /* hide when notif or sessions is open to avoid overlap */
`;

export default function MentorsPage() {
  const navigate = useNavigate();
  const timerRef      = useRef(null);
  const endRef        = useRef(null);
  const activeChatRef = useRef(null);

  const [dark,            setDark]            = useState(() => localStorage.getItem("theme") === "dark-theme");
  const [mentors,         setMentors]         = useState([]);
  const [loading,         setLoading]         = useState(true);
  const [search,          setSearch]          = useState("");
  const [category,        setCategory]        = useState("");
  const [sortBy,          setSortBy]          = useState("rating");
  const [selectedMentor,  setSelectedMentor]  = useState(null);
  const [showBooking,     setShowBooking]     = useState(false);
  const [formData,        setFormData]        = useState({user_name:"",user_email:"",user_phone:"",topic:"",date:"",time:"",message:""});
  const [slots,           setSlots]           = useState([]);
  const [slotsLoading,    setSlotsLoading]    = useState(false);
  const [dayOffMsg,       setDayOffMsg]       = useState("");
  const [bookingLoading,  setBookingLoading]  = useState(false);
  const [bookingAlert,    setBookingAlert]    = useState(null);
  const [confirmation,    setConfirmation]    = useState(null);
  const [showSessions,    setShowSessions]    = useState(false);
  const [sessionsTab,     setSessionsTab]     = useState("upcoming");
  const [allSessions,     setAllSessions]     = useState([]);
  const [showChat,        setShowChat]        = useState(false);
  const [activeChat,      setActiveChat]      = useState(null);
  const [chatMsgs,        setChatMsgs]        = useState([]);
  const [newMsg,          setNewMsg]          = useState("");
  const [chatTab,         setChatTab]         = useState("chat");
  const [timeLeft,        setTimeLeft]        = useState(null);
  const [showCancel,      setShowCancel]      = useState(false);
  const [cancelAppt,      setCancelAppt]      = useState(null);
  const [cancelLoading,   setCancelLoading]   = useState(false);
  const [showFeedback,    setShowFeedback]    = useState(false);
  const [feedbackData,    setFeedbackData]    = useState({rating:0,review:"",mentorId:null,appointmentId:null});
  const [fbLoading,       setFbLoading]       = useState(false);
  const [showReport,      setShowReport]      = useState(false);
  const [reportData,      setReportData]      = useState({category:"",description:"",appointmentId:null,mentorId:null});
  const [notifs,          setNotifs]          = useState([]);
  const [unread,          setUnread]          = useState(0);
  const [showNotif,       setShowNotif]       = useState(false);
  const [toasts,          setToasts]          = useState([]);
  const [confirmLoading,  setConfirmLoading]  = useState(false);
  const [now,             setNow]             = useState(new Date());
  const [expandedGuide,   setExpandedGuide]   = useState(null);

  useEffect(() => { activeChatRef.current = activeChat; }, [activeChat]);
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 30000); return () => clearInterval(t); }, []);

  const toast = useCallback((msg, type="success") => {
    const id = Date.now();
    setToasts(p => [...p, {id,msg,type}]);
    setTimeout(() => setToasts(p => p.filter(t => t.id !== id)), 4000);
  }, []);

  const isAdmin = useCallback(() => auth.currentUser?.email === ADMIN_EMAIL, []);

  useEffect(() => {
    loadMentors();
    if (auth.currentUser) { fetchSessions(); fetchNotifs(); }
    const cleanup = subscribeRT();
    return () => { cleanup(); if(timerRef.current) clearInterval(timerRef.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadMentors = async () => {
    setLoading(true);
    const {data} = await supabase.from("mentors")
      .select("id,full_name,email,profession,organization,bio,expertise,category,session_price,profile_photo_url,rating,total_reviews,sessions_count,linkedin_verified")
      .eq("is_active",true).eq("is_banned",false).eq("is_on_break",false);
    setMentors((data||[]).map(m => ({...m, badge: calculateBadge(m.sessions_count||0)})));
    setLoading(false);
  };

  const subscribeRT = () => {
    const ch = supabase.channel("mpv2-rt")
      .on("postgres_changes",{event:"*",schema:"public",table:"chat_messages"},(p) => {
        const current = activeChatRef.current;
        if (current && p.new.chat_session_id===current.id && p.new.sender_role==="mentor") {
          setChatMsgs(prev => prev.some(m => m.id===p.new.id) ? prev : [...prev, p.new]);
          setTimeout(() => endRef.current?.scrollIntoView({behavior:"smooth"}), 50);
        }
      })
      .on("postgres_changes",{event:"INSERT",schema:"public",table:"mentor_notifications"}, fetchNotifs)
      .on("postgres_changes",{event:"UPDATE",schema:"public",table:"appointments"},(p) => {
        setAllSessions(prev => prev.map(s => s.id===p.new.id ? {...s,...p.new,mentorName:s.mentorName} : s));
        fetchSessions();
      })
      .subscribe();
    return () => supabase.removeChannel(ch);
  };

  const fetchSessions = async () => {
    const email = auth.currentUser?.email;
    if (!email) return;
    try {
      const {data, error} = await supabase.from("appointments")
        .select("*,mentor:mentor_id(id,full_name,profession,profile_photo_url)")
        .eq("user_email", email)
        .not("status","in",'("pending_payment")')
        .order("date",{ascending:true}).order("time",{ascending:true});
      if (error) { console.error("fetchSessions error:", error); return; }
      setAllSessions((data||[]).map(item => ({
        ...item,
        time: normalizeTime(item.time),
        mentorName: item.mentor?.full_name || item.mentor_name || "Unknown Mentor",
      })));
    } catch(err) { console.error("fetchSessions exception:", err); }
  };

  const fetchNotifs = async () => {
    const email = auth.currentUser?.email; if(!email) return;
    const {data} = await supabase.from("mentor_notifications").select("*")
      .eq("recipient",email).order("created_at",{ascending:false}).limit(20);
    setNotifs(data||[]);
    setUnread((data||[]).filter(n => !n.is_read).length);
  };

  const { upcoming, active, completed, cancelled } = useMemo(() => {
    const up=[], ac=[], co=[], ca=[];
    allSessions.forEach(item => {
      if (["cancelled_by_user","cancelled_by_mentor","refunded"].includes(item.status)) { ca.push(item); return; }
      const start = buildSessionDate(item.date, item.time);
      const end   = new Date(start.getTime() + 3600000);
      if (end <= now)            co.push(item);
      else if (start <= now)     ac.push(item);
      else                       up.push(item);
    });
    return {upcoming:up, active:ac, completed:co, cancelled:ca};
  }, [allSessions, now]);

  const filteredMentors = useMemo(() => {
    let list = [...mentors];
    const q = search.trim().toLowerCase();
    if (q) list = list.filter(m => m.full_name?.toLowerCase().includes(q)||m.expertise?.toLowerCase().includes(q)||m.profession?.toLowerCase().includes(q));
    if (category) list = list.filter(m => m.category===category);
    list.sort((a,b) => {
      if(sortBy==="rating")     return (b.rating||0)-(a.rating||0);
      if(sortBy==="price_asc")  return (a.session_price||200)-(b.session_price||200);
      if(sortBy==="sessions")   return (b.sessions_count||0)-(a.sessions_count||0);
      return 0;
    });
    return list;
  }, [mentors, search, category, sortBy]);

  const openBooking = (mentor) => {
    if(!auth.currentUser){toast("Please log in first","error");navigate("/");return;}
    setSelectedMentor(mentor);
    setFormData({user_name:auth.currentUser.displayName||"",user_email:auth.currentUser.email||"",user_phone:"",topic:"",date:"",time:"",message:""});
    setSlots([]); setDayOffMsg(""); setBookingAlert(null); setShowBooking(true);
  };

  const handleDateChange = async (date) => {
    setFormData(p => ({...p,date,time:""})); setDayOffMsg("");
    if(!date||!selectedMentor) return;
    setSlotsLoading(true);
    const result = await getAvailableSlots(selectedMentor.id, date);
    setSlotsLoading(false);
    if(result==="day_off") { setDayOffMsg("Not available on this date. Please choose another."); setSlots([]); }
    else setSlots(result||[]);
  };

  const handleAdminBooking = async () => {
    if(!formData.time){setBookingAlert({type:"error",msg:"Please select a time"});return;}
    setBookingLoading(true);
    try {
      const time24=convertTo24(formData.time);
      const start=new Date(`${formData.date}T${time24}`);
      const {data:appt,error}=await supabase.from("appointments").insert({
        mentor_id:selectedMentor.id,mentor_name:selectedMentor.full_name,mentor_email:selectedMentor.email,
        user_name:formData.user_name||"Admin",user_email:formData.user_email,user_phone:formData.user_phone||null,
        topic:formData.topic||"Admin Session",date:formData.date,time:time24,message:formData.message||null,
        session_price:0,platform_fee:0,total_paid:0,status:"confirmed",
        auto_confirm_at:new Date(start.getTime()+7200000).toISOString(),
      }).select().single();
      if(error) throw error;
      await supabase.from("chat_sessions").insert({appointment_id:appt.id,mentor_id:selectedMentor.id,status:"active",scheduled_start:start.toISOString(),scheduled_end:new Date(start.getTime()+3600000).toISOString()});
      await supabase.from("mentor_notifications").insert({recipient:selectedMentor.email,type:"new_appointment",title:"Admin Session Booked",message:`Admin session on ${formData.date} at ${formData.time}`,is_read:false,created_at:new Date().toISOString()});
      setShowBooking(false); toast("✅ Admin session booked!"); fetchSessions();
    } catch(err){setBookingAlert({type:"error",msg:err.message});}
    finally{setBookingLoading(false);}
  };

  const handleBooking = async () => {
    if(!formData.time){setBookingAlert({type:"error",msg:"Please select a time slot"});return;}
    if(isAdmin()){await handleAdminBooking();return;}
    if(!formData.user_name.trim()){setBookingAlert({type:"error",msg:"Please enter your name"});return;}
    if(!formData.topic.trim()){setBookingAlert({type:"error",msg:"Please enter a topic"});return;}
    const emailRegex=/^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if(!emailRegex.test(formData.user_email)){setBookingAlert({type:"error",msg:"Please enter a valid email"});return;}
    setBookingLoading(true); setBookingAlert(null);
    try {
      const total=(selectedMentor.session_price+PLATFORM_FEE)*100;
      const res=await fetch(`${BACKEND_URL}/api/payment/create-order`,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({amount:total,sessionPrice:selectedMentor.session_price,platformFee:PLATFORM_FEE,mentorId:selectedMentor.id,mentorName:selectedMentor.full_name,userEmail:formData.user_email,userName:formData.user_name,topic:formData.topic,date:formData.date,time:convertTo24(formData.time)})});
      if(!res.ok){const e=await res.json();throw new Error(e.error||"Order creation failed");}
      const {orderId,appointmentId}=await res.json();
      if(!window.Razorpay) throw new Error("Payment gateway not loaded. Please refresh.");
      const rzp=new window.Razorpay({
        key:RAZORPAY_KEY,amount:total,currency:"INR",name:"HealHub",description:`Session with ${selectedMentor.full_name}`,order_id:orderId,
        prefill:{name:formData.user_name,email:formData.user_email,contact:formData.user_phone||""},
        theme:{color:"#5A6993"},
        handler:async(response)=>{
          const vRes=await fetch(`${BACKEND_URL}/api/payment/verify`,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({razorpay_order_id:response.razorpay_order_id,razorpay_payment_id:response.razorpay_payment_id,razorpay_signature:response.razorpay_signature,appointmentId,mentorId:selectedMentor.id,mentorName:selectedMentor.full_name,mentorEmail:selectedMentor.email,userName:formData.user_name,userEmail:formData.user_email,userPhone:formData.user_phone,topic:formData.topic,date:formData.date,time:convertTo24(formData.time),message:formData.message,sessionPrice:selectedMentor.session_price,platformFee:PLATFORM_FEE})});
          if(vRes.ok){
            setShowBooking(false);
            setConfirmation({mentorName:selectedMentor.full_name,date:formData.date,time:formData.time,topic:formData.topic,sessionPrice:selectedMentor.session_price,platformFee:PLATFORM_FEE,total:selectedMentor.session_price+PLATFORM_FEE,paymentId:response.razorpay_payment_id,orderId:response.razorpay_order_id,email:formData.user_email});
            fetchSessions();
          } else toast("Payment verification failed. If charged, contact support.","error");
          setBookingLoading(false);
        },
        modal:{ondismiss:()=>{setBookingLoading(false);toast("Payment cancelled","info");}},
      });
      rzp.open();
    } catch(err){setBookingAlert({type:"error",msg:err.message});setBookingLoading(false);}
  };

  const openSessionChat = async (appointment) => {
    let {data:chat}=await supabase.from("chat_sessions").select("*").eq("appointment_id",appointment.id).maybeSingle();
    if(!chat){
      const start=new Date(`${appointment.date}T${normalizeTime(appointment.time)}`);
      const {data}=await supabase.from("chat_sessions").insert({
        appointment_id:appointment.id,mentor_id:appointment.mentor_id,
        status:"active",scheduled_start:start.toISOString(),
        scheduled_end:new Date(start.getTime()+3600000).toISOString()
      }).select().single();
      chat=data;
    }
    const {data:msgs}=await supabase.from("chat_messages").select("*").eq("chat_session_id",chat.id).order("created_at",{ascending:true});
    const chatObj = {...chat, appointment};
    setActiveChat(chatObj); activeChatRef.current=chatObj;
    setChatMsgs(msgs||[]); setChatTab("chat"); setShowChat(true);
    setTimeout(() => endRef.current?.scrollIntoView({behavior:"smooth"}), 100);
    if(chat.scheduled_end){
      if(timerRef.current) clearInterval(timerRef.current);
      const tick=()=>{const r=new Date(chat.scheduled_end)-new Date();if(r<=0){clearInterval(timerRef.current);setTimeLeft(0);}else setTimeLeft(r);};
      tick(); timerRef.current=setInterval(tick,1000);
    }
  };

  const sendMessage = async () => {
    if(!newMsg.trim()||!activeChat) return;
    const tmp={id:`tmp-${Date.now()}`,chat_session_id:activeChat.id,sender_role:"user",sender_name:auth.currentUser?.displayName||auth.currentUser?.email||"User",message:newMsg,created_at:new Date().toISOString()};
    setChatMsgs(p => [...p,tmp]); setNewMsg("");
    setTimeout(() => endRef.current?.scrollIntoView({behavior:"smooth"}), 50);
    const {data,error}=await supabase.from("chat_messages").insert({chat_session_id:activeChat.id,appointment_id:activeChat.appointment_id,sender_role:"user",sender_name:tmp.sender_name,message:tmp.message}).select().single();
    if(error){setChatMsgs(p => p.filter(m => m.id!==tmp.id));toast("Failed to send","error");}
    else setChatMsgs(p => p.map(m => m.id===tmp.id ? data : m));
  };

  const confirmSession = async (aptId) => {
    setConfirmLoading(true);
    try {
      const res=await fetch(`${BACKEND_URL}/api/payment/confirm-session`,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({appointmentId:aptId,userEmail:auth.currentUser?.email})});
      if(res.ok){setAllSessions(p => p.map(a => a.id===aptId ? {...a,session_confirmed_by_user:true} : a));toast("✅ Session confirmed! Mentor payout initiated.");}
      else toast("Confirmation failed","error");
    } catch{toast("Error","error");}
    finally{setConfirmLoading(false);}
  };

  const handleCancelConfirm = async () => {
    if(!cancelAppt) return;
    setCancelLoading(true);
    try {
      const res=await fetch(`${BACKEND_URL}/api/payment/cancel`,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({appointmentId:cancelAppt.id,userEmail:auth.currentUser?.email})});
      const data=await res.json();
      if(res.ok){toast(`Cancelled. Refund of ₹${data.refundAmount} initiated.`);setShowCancel(false);fetchSessions();}
      else toast(data.error||"Cancellation failed","error");
    } catch{toast("Error cancelling","error");}
    finally{setCancelLoading(false);}
  };

  const submitFeedback = async () => {
    if(!feedbackData.rating){toast("Please select a rating","error");return;}
    setFbLoading(true);
    try {
      await supabase.from("mentor_feedback").insert({mentor_id:feedbackData.mentorId,appointment_id:feedbackData.appointmentId,user_email:auth.currentUser?.email,rating:feedbackData.rating,comment:feedbackData.review||null,created_at:new Date().toISOString()});
      await supabase.from("appointments").update({status:"completed_with_feedback"}).eq("id",feedbackData.appointmentId);
      setAllSessions(p => p.map(a => a.id===feedbackData.appointmentId ? {...a,status:"completed_with_feedback"} : a));
      setShowFeedback(false); toast("⭐ Thank you for your feedback!");
    } catch{toast("Failed to submit","error");}
    finally{setFbLoading(false);}
  };

  const submitReport = async () => {
    if(!reportData.category||!reportData.description.trim()){toast("Please fill all fields","error");return;}
    try {
      await fetch(`${BACKEND_URL}/api/reports/file`,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({mentorId:reportData.mentorId,appointmentId:reportData.appointmentId,reporterEmail:auth.currentUser?.email,category:reportData.category,description:reportData.description})});
      setShowReport(false); toast("Report submitted. We'll review within 48 hours.");
    } catch{toast("Failed","error");}
  };

  const markRead = async () => {
    await supabase.from("mentor_notifications").update({is_read:true}).eq("recipient",auth.currentUser?.email).eq("is_read",false);
    setUnread(0); setNotifs(p => p.map(n => ({...n,is_read:true})));
  };

  const getHrs = (apt) => (buildSessionDate(apt.date, apt.time) - now) / 3600000;
  const canCancel = (apt) => getHrs(apt) > 0 && !["cancelled_by_user","cancelled_by_mentor"].includes(apt.status);

  const getJoinState = (apt) => {
    const start = buildSessionDate(apt.date, apt.time);
    const end   = new Date(start.getTime() + 3600000);
    const opens = new Date(start.getTime() - 5*60000);
    if (now >= opens && now <= end) return "join_now";
    if (now > end) return "ended";
    return "too_early";
  };

  const getCountdown = (apt) => {
    const start  = buildSessionDate(apt.date, apt.time);
    const opens  = new Date(start.getTime() - 5*60000);
    const diffMs = opens - now;
    if (diffMs <= 0) return null;
    const totalMin = Math.ceil(diffMs/60000);
    if (totalMin >= 1440) { const d=Math.floor(totalMin/1440); return `${d} day${d>1?"s":""}`; }
    if (totalMin >= 60)   { const h=Math.floor(totalMin/60), m=totalMin%60; return m?`${h}h ${m}m`:`${h}h`; }
    return `${totalMin} min`;
  };

  const todayDate = new Date().toISOString().split("T")[0];
  const totalSessions = upcoming.length + active.length + completed.length;

  const renderStars = (rating, reviews) => {
    if(!reviews||reviews<3) return <span style={{fontSize:".76rem",color:"var(--muted)",fontWeight:600}}>New</span>;
    const f=Math.floor(rating), h=rating-f>=0.5;
    return (
      <>
        <span className="mp-stars">{"★".repeat(f)}{h?"½":""}{"☆".repeat(5-f-(h?1:0))}</span>
        <span className="mp-rval">{parseFloat(rating).toFixed(1)}</span>
        <span className="mp-rct">({reviews})</span>
      </>
    );
  };

  const formatDisplayTime = (rawTime) => {
    const t = normalizeTime(rawTime);
    const [h,m] = t.split(":");
    let hour = parseInt(h,10);
    const ampm = hour>=12?"PM":"AM";
    if (hour>12) hour-=12;
    if (hour===0) hour=12;
    return `${String(hour).padStart(2,"0")}:${m} ${ampm}`;
  };

  return (
    <>
      <style>{css}</style>
      {!window.Razorpay && <script src="https://checkout.razorpay.com/v1/checkout.js" async/>}

      <div className={`mp-root${dark?" mp-dark":""}`}>

        {/* ── HEADER ── */}
        <div className="mp-hdr">
          <div className="mp-hdr-inner">
            <div className="mp-hdr-top">
              <button className="mp-hdr-back" onClick={() => navigate("/dashboard")}>← Back</button>
              <div className="mp-hdr-actions">
                {auth.currentUser && (
                  <button
                    className="mp-hdr-btn"
                    onClick={() => { setShowSessions(true); setSessionsTab(active.length?"active":"upcoming"); }}
                  >
                    📅 Sessions
                    {totalSessions>0 && <span className="mp-ct-badge" style={{position:"relative",top:"auto",right:"auto"}}>{totalSessions}</span>}
                  </button>
                )}
                <button
                  className="mp-hdr-btn" style={{position:"relative"}}
                  onClick={() => { setShowNotif(v => !v); markRead(); }}
                >
                  🔔
                  {unread>0 && <span className="mp-ct-badge">{unread}</span>}
                </button>
                <button className="mp-hdr-btn" onClick={() => { setDark(d=>!d); localStorage.setItem("theme", !dark?"dark-theme":"light-theme"); }}>
                  {dark?"☀️":"🌙"}
                </button>
              </div>
            </div>

            <div className="mp-hdr-hero">
              <div className="mp-hdr-eyebrow">💙 Verified Professionals</div>
              <h1>Find Your Mentor</h1>
              <p>Book a private 60-minute session with a verified wellness professional</p>
            </div>
          </div>
        </div>

        {/* ── NOTIF DROPDOWN ── */}
        {showNotif && (
          <div className="mp-notif-drop">
            <div className="mp-notif-hdr">
              Notifications
              <button className="mp-notif-close" onClick={() => setShowNotif(false)}>✕</button>
            </div>
            {notifs.length===0
              ? <div style={{padding:"28px 14px",textAlign:"center",color:"var(--muted)",fontSize:".84rem"}}>All caught up ✅</div>
              : notifs.map(n => (
                <div key={n.id} className={`mp-notif-row${n.is_read?"":" unread"}`}>
                  <div className="mp-notif-title">{n.title}</div>
                  <div className="mp-notif-msg">{n.message}</div>
                  <div className="mp-notif-date">{new Date(n.created_at).toLocaleDateString()}</div>
                </div>
              ))}
          </div>
        )}

        {/* ── LIVE BANNER ── */}
        {active.length>0 && (
          <div className="mp-live-banner">
            <div className="mp-live-banner-text">
              <div className="mp-live-dot"/>
              {active.length} active session{active.length>1?"s":""} happening right now
            </div>
            <button className="mp-live-join-btn" onClick={() => { setShowSessions(true); setSessionsTab("active"); }}>
              Join Now →
            </button>
          </div>
        )}

        {/* ── FILTERS ── */}
        <div className="mp-filters-wrap">
          <div className="mp-filters-card">
            <div className="mp-search-wrap">
              <span className="mp-search-icon">🔍</span>
              <input
                className="mp-search"
                placeholder="Search by name, expertise, profession…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
            <div className="mp-filter-row">
              <select className="mp-select" value={sortBy} onChange={e => setSortBy(e.target.value)}>
                <option value="rating">⭐ Top Rated</option>
                <option value="price_asc">💰 Lowest Price</option>
                <option value="sessions">📅 Most Sessions</option>
              </select>
            </div>
            <div className="mp-cat-scroll">
              {CATEGORIES.map(c => (
                <button
                  key={c.value}
                  className={`mp-cat-chip${category===c.value?" on":""}`}
                  onClick={() => setCategory(c.value)}
                >
                  {c.icon} {c.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* ── GRID ── */}
        <div className="mp-content">
          <div className="mp-result-line">
            {loading ? "Loading mentors…" : `${filteredMentors.length} mentor${filteredMentors.length!==1?"s":""} available`}
          </div>

          {loading ? (
            <div className="mp-grid">
              {[1,2,3,4,5,6].map(i => (
                <div key={i} className="mp-skel">
                  <div className="mp-skel-top mp-shimmer"/>
                  <div className="mp-skel-body">
                    <div className="mp-skel-ln mp-shimmer" style={{width:"55%",margin:"0 auto 9px"}}/>
                    <div className="mp-skel-ln mp-shimmer" style={{width:"35%",margin:"0 auto 9px"}}/>
                    <div className="mp-skel-ln mp-shimmer" style={{width:"75%",margin:"0 auto"}}/>
                  </div>
                </div>
              ))}
            </div>
          ) : filteredMentors.length===0 ? (
            <div className="mp-grid">
              <div className="mp-empty">
                <span className="mp-empty-icon">🔍</span>
                <strong style={{display:"block",fontSize:"1rem",color:"var(--ink)",marginBottom:6}}>No mentors found</strong>
                <span style={{fontSize:".84rem"}}>Try adjusting your search or filters</span>
              </div>
            </div>
          ) : (
            <div className="mp-grid">
              {filteredMentors.map((m, idx) => (
                <div
                  key={m.id}
                  className="mp-card"
                  style={{animationDelay:`${idx*0.04}s`}}
                  onClick={() => openBooking(m)}
                >
                  <div className="mp-card-photo-wrap">
                    {m.profile_photo_url
                      ? <img className="mp-avatar" src={m.profile_photo_url} alt={m.full_name} loading="lazy"/>
                      : <div className="mp-avatar-ph">👤</div>}
                    <div className="mp-card-badge">{m.badge}</div>
                    {m.linkedin_verified && <div className="mp-li-badge">🔗 Verified</div>}
                  </div>
                  <div className="mp-card-body">
                    <div className="mp-name">{m.full_name}</div>
                    <div className="mp-role">{m.profession}</div>
                    <div className="mp-rating">{renderStars(m.rating, m.total_reviews)}</div>
                    {m.expertise && (
                      <div className="mp-tags">
                        {m.expertise.split(",").slice(0,3).map(e => <span key={e} className="mp-tag">{e.trim()}</span>)}
                      </div>
                    )}
                    <div className="mp-price">₹{m.session_price+PLATFORM_FEE}<span> / session</span></div>
                    <button className="mp-book-btn">Book Session →</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ─── BOOKING MODAL ─── */}
      {showBooking && selectedMentor && (
        <div className="mp-overlay" onClick={e => e.target===e.currentTarget&&!bookingLoading&&setShowBooking(false)}>
          <div className="mp-modal">
            <div className="mp-modal-handle"/>
            <div className="mp-modal-hdr">
              <div className="mp-modal-title">Book a Session</div>
              <button className="mp-modal-close" onClick={() => !bookingLoading&&setShowBooking(false)}>✕</button>
            </div>
            <div className="mp-modal-body">
              {bookingAlert && <div className={`mp-alert ${bookingAlert.type}`}>{bookingAlert.msg}</div>}

              <div className="mp-security">
                🔒 <span>Payments via <strong>Razorpay</strong> (PCI-DSS). HealHub never stores your card details.</span>
              </div>

              <div className="mp-mentor-preview">
                {selectedMentor.profile_photo_url
                  ? <img className="mp-preview-av" src={selectedMentor.profile_photo_url} alt={selectedMentor.full_name}/>
                  : <div className="mp-preview-av-ph">👤</div>}
                <div>
                  <div style={{fontFamily:"var(--font-display)",fontWeight:700,fontSize:".98rem",color:"var(--ink)"}}>{selectedMentor.full_name}</div>
                  <div style={{fontSize:".78rem",color:"var(--muted)",marginTop:2}}>{selectedMentor.profession}</div>
                  {selectedMentor.linkedin_verified && <div style={{fontSize:".72rem",color:"#0077b5",marginTop:3}}>🔗 LinkedIn Verified</div>}
                </div>
              </div>

              {isAdmin() && <div className="mp-alert info">🛡️ Admin booking — payment bypassed.</div>}

              <div className="mp-fg-row">
                <div className="mp-fg"><label>Your Name *</label><input placeholder="Full name" value={formData.user_name} onChange={e => setFormData(p => ({...p,user_name:e.target.value}))}/></div>
                <div className="mp-fg"><label>Phone (optional)</label><input placeholder="+91 98765 43210" value={formData.user_phone} onChange={e => setFormData(p => ({...p,user_phone:e.target.value}))}/></div>
              </div>
              <div className="mp-fg"><label>Email *</label><input type="email" placeholder="your@email.com" value={formData.user_email} onChange={e => setFormData(p => ({...p,user_email:e.target.value}))}/></div>
              <div className="mp-fg"><label>What do you want to discuss? *</label><input placeholder="e.g. Managing work anxiety" value={formData.topic} onChange={e => setFormData(p => ({...p,topic:e.target.value}))} maxLength={200}/></div>
              <div className="mp-fg"><label>Date *</label><input type="date" min={todayDate} value={formData.date} onChange={e => handleDateChange(e.target.value)}/></div>

              {dayOffMsg && <div className="mp-alert error">{dayOffMsg}</div>}

              {formData.date && !dayOffMsg && (
                <div className="mp-fg">
                  <label>Time Slot *</label>
                  {slotsLoading
                    ? <div style={{textAlign:"center",color:"var(--muted)",padding:"16px 0",fontSize:".84rem"}}>Loading available slots…</div>
                    : slots.length===0
                      ? <div className="mp-alert info">No slots on this date.{formData.date===todayDate?" Slots need 1hr advance notice. ":""} Try another date.</div>
                      : <div className="mp-slot-grid">{slots.map(s => <div key={s} className={`mp-slot${formData.time===s?" selected":""}`} onClick={() => setFormData(p => ({...p,time:s}))}>{s}</div>)}</div>}
                </div>
              )}

              {formData.date===todayDate && <div className="mp-alert info" style={{fontSize:".76rem"}}>⏰ Slots require at least 1hr advance notice.</div>}

              <div className="mp-fg"><label>Notes (optional)</label><textarea rows={2} placeholder="Context for the mentor…" value={formData.message} onChange={e => setFormData(p => ({...p,message:e.target.value}))}/></div>

              {!isAdmin() && (
                <div className="mp-price-box">
                  <div className="mp-price-row"><span>Session fee</span><span>₹{selectedMentor.session_price}</span></div>
                  <div className="mp-price-row"><span>Platform fee</span><span>₹{PLATFORM_FEE}</span></div>
                  <div className="mp-price-row"><span>Total</span><span>₹{selectedMentor.session_price+PLATFORM_FEE}</span></div>
                </div>
              )}

              <div style={{fontSize:".76rem",color:"var(--muted)",marginBottom:14,lineHeight:1.65}}>
                💡 Sessions are 60 minutes. Cancel free 12+ hrs before — 15% fee for late cancellations.
              </div>

              <button
                className="mp-btn-primary"
                onClick={handleBooking}
                disabled={bookingLoading||!formData.time||!formData.date||!formData.topic.trim()||!!dayOffMsg}
              >
                {bookingLoading
                  ? <><span className="mp-spin"/> Processing…</>
                  : isAdmin()
                    ? "✅ Book Admin Session (Free)"
                    : `💳 Pay ₹${selectedMentor.session_price+PLATFORM_FEE} & Confirm`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── BOOKING CONFIRMATION ─── */}
      {confirmation && (
        <div className="mp-overlay">
          <div className="mp-confirm-modal">
            <div className="mp-confirm-hero">
              <span className="mp-confirm-icon">🎉</span>
              <h2 style={{margin:"0 0 6px",fontSize:"1.35rem",fontFamily:"var(--font-display)",fontWeight:700}}>Session Confirmed!</h2>
              <p style={{opacity:.88,fontSize:".84rem",margin:0}}>Confirmation sent to {confirmation.email}</p>
            </div>
            <div className="mp-confirm-body">
              <div className="mp-confirm-section">
                <h4>Session Details</h4>
                <div className="mp-confirm-row"><span className="mp-confirm-label">Mentor</span><span className="mp-confirm-val">{confirmation.mentorName}</span></div>
                <div className="mp-confirm-row"><span className="mp-confirm-label">Date</span><span className="mp-confirm-val">{new Date(confirmation.date+"T12:00:00").toLocaleDateString("en-IN",{weekday:"long",day:"numeric",month:"long",year:"numeric"})}</span></div>
                <div className="mp-confirm-row"><span className="mp-confirm-label">Time</span><span className="mp-confirm-val">{confirmation.time}</span></div>
                <div className="mp-confirm-row"><span className="mp-confirm-label">Topic</span><span className="mp-confirm-val">{confirmation.topic}</span></div>
                <div className="mp-confirm-row"><span className="mp-confirm-label">Duration</span><span className="mp-confirm-val">60 minutes</span></div>
              </div>
              <div className="mp-confirm-section">
                <h4>Payment</h4>
                <div className="mp-confirm-row"><span className="mp-confirm-label">Session</span><span className="mp-confirm-val">₹{confirmation.sessionPrice}</span></div>
                <div className="mp-confirm-row"><span className="mp-confirm-label">Platform</span><span className="mp-confirm-val">₹{confirmation.platformFee}</span></div>
                <div className="mp-confirm-row"><span className="mp-confirm-label">Total charged</span><span className="mp-confirm-val" style={{color:"var(--success)"}}>₹{confirmation.total}</span></div>
              </div>
              <div className="mp-confirm-section">
                <h4>Reference</h4>
                <div className="mp-confirm-id">Payment ID: {confirmation.paymentId}</div>
                <div style={{fontSize:".76rem",color:"var(--muted)",lineHeight:1.75}}>
                  📅 Join from <strong>My Sessions → Active</strong> when your session starts<br/>
                  ❌ Cancel from <strong>My Sessions → Upcoming</strong> (free if 12+ hrs before)<br/>
                  📧 Confirmation sent to <strong>{confirmation.email}</strong>
                </div>
              </div>
              <div style={{display:"flex",gap:10,marginTop:16}}>
                <button onClick={() => setConfirmation(null)} className="mp-btn-ghost" style={{width:"auto",padding:"11px 18px"}}>Close</button>
                <button
                  onClick={() => {setConfirmation(null);setShowSessions(true);setSessionsTab("upcoming");}}
                  className="mp-btn-primary" style={{flex:1}}
                >View My Sessions →</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── MY SESSIONS MODAL ─── */}
      {showSessions && (
        <div className="mp-overlay" onClick={e => e.target===e.currentTarget&&setShowSessions(false)}>
          <div className="mp-sessions-modal">
            <div className="mp-modal-handle"/>
            <div className="mp-sessions-hdr">
              <div>
                <div style={{fontFamily:"var(--font-display)",fontWeight:700,fontSize:"1.05rem",color:"var(--ink)"}}>My Sessions</div>
                <div style={{fontSize:".76rem",color:"var(--muted)",marginTop:2}}>All your bookings</div>
              </div>
              <button className="mp-modal-close" onClick={() => setShowSessions(false)}>✕</button>
            </div>
            <div className="mp-stabs">
              {[
                {id:"active",    label:`🔴 Live (${active.length})`},
                {id:"upcoming",  label:`📅 Upcoming (${upcoming.length})`},
                {id:"completed", label:`✅ Done (${completed.length})`},
                {id:"cancelled", label:`❌ Cancelled (${cancelled.length})`},
              ].map(t => (
                <button key={t.id} className={`mp-stab${sessionsTab===t.id?" active":""}`} onClick={() => setSessionsTab(t.id)}>{t.label}</button>
              ))}
            </div>
            <div className="mp-sbody">

              {/* ACTIVE */}
              {sessionsTab==="active" && (
                active.length===0
                  ? <div className="mp-empty-state"><span className="mp-empty-state-icon">🕐</span>No active sessions right now</div>
                  : active.map(apt => (
                    <div key={apt.id} className="mp-apt live">
                      <div className="mp-apt-top">
                        <div className="mp-apt-name">{apt.mentorName}</div>
                        <span className="mp-apt-badge live">● Live</span>
                      </div>
                      <div className="mp-apt-info">📅 {apt.date} at {formatDisplayTime(apt.time)}</div>
                      <div className="mp-apt-topic">📝 {apt.topic}</div>
                      <div className="mp-receipt">
                        <div className="mp-receipt-row total"><span>Total paid</span><span>₹{apt.total_paid}</span></div>
                      </div>
                      <div className="mp-apt-actions">
                        <button className="mp-join" onClick={() => {setShowSessions(false);openSessionChat(apt);}}>💬 Chat</button>
                        <button className="mp-join-video-soon" onClick={() => {setShowSessions(false);openSessionChat(apt);setTimeout(()=>setChatTab("video"),300);}}>📹 Video</button>
                        {!apt.session_confirmed_by_user && (
                          <button className="mp-confirm-btn" onClick={() => confirmSession(apt.id)} disabled={confirmLoading}>
                            {confirmLoading?"…":"✅ Confirm Done"}
                          </button>
                        )}
                      </div>
                    </div>
                  ))
              )}

              {/* UPCOMING */}
              {sessionsTab==="upcoming" && (
                upcoming.length===0
                  ? <div className="mp-empty-state">
                      <span className="mp-empty-state-icon">📅</span>
                      <div style={{fontWeight:700,marginBottom:6,color:"var(--ink)"}}>No upcoming sessions</div>
                      <div style={{fontSize:".82rem",marginBottom:14}}>Find a mentor and book your first session</div>
                      <button className="mp-btn-primary" style={{width:"auto",padding:"10px 22px"}} onClick={() => setShowSessions(false)}>Browse Mentors →</button>
                    </div>
                  : upcoming.map(apt => {
                      const hrs       = getHrs(apt);
                      const joinState = getJoinState(apt);
                      const countdown = getCountdown(apt);
                      const refund    = calcCancellationRefund(apt.session_price, hrs);
                      const isGuide   = expandedGuide===apt.id;
                      const sessionDate = new Date(apt.date+"T12:00:00").toLocaleDateString("en-IN",{weekday:"long",day:"numeric",month:"long",year:"numeric"});
                      return (
                        <div key={apt.id} className="mp-apt" style={{borderColor:joinState==="join_now"?"var(--success)":"var(--sand-light)",background:joinState==="join_now"?"var(--success-wash)":"var(--card)"}}>
                          <div className="mp-apt-top">
                            <div className="mp-apt-name">{apt.mentorName}</div>
                            {joinState==="join_now"
                              ? <span className="mp-apt-badge" style={{background:"var(--success)",color:"#fff"}}>Ready</span>
                              : <span className="mp-apt-badge upcoming">Upcoming</span>}
                          </div>
                          <div className="mp-apt-info">📅 {sessionDate}</div>
                          <div className="mp-apt-info">⏰ {formatDisplayTime(apt.time)} · 60 minutes</div>
                          <div className="mp-apt-topic">📝 {apt.topic}</div>
                          {joinState==="too_early" && countdown && <div className="mp-countdown-pill">🔒 Join opens in {countdown}</div>}
                          {joinState==="join_now" && <div className="mp-soon-pill">✅ Session open — join now!</div>}
                          {hrs>0 && hrs<=1 && joinState==="too_early" && (
                            <div style={{fontSize:".76rem",color:"#8B4513",background:"var(--peach-wash)",padding:"5px 10px",borderRadius:"var(--r-md)",marginBottom:8,fontWeight:600,border:"1px solid rgba(230,161,132,.2)"}}>
                              ⏰ Starting in {Math.round(hrs*60)} minute{Math.round(hrs*60)!==1?"s":""} — get ready!
                            </div>
                          )}
                          <div className="mp-apt-actions" style={{marginBottom:10}}>
                            {joinState==="join_now" ? (
                              <>
                                <button className="mp-join-soon" onClick={() => {setShowSessions(false);openSessionChat(apt);}}>💬 Join Chat</button>
                                <button className="mp-join-video-soon" onClick={() => {setShowSessions(false);openSessionChat(apt);setTimeout(()=>setChatTab("video"),300);}}>📹 Video</button>
                              </>
                            ) : (
                              <button className="mp-join-locked" disabled>
                                🔒 Opens at {(() => {const d=buildSessionDate(apt.date,apt.time);d.setMinutes(d.getMinutes()-5);return d.toLocaleTimeString("en-IN",{hour:"2-digit",minute:"2-digit"});})()}
                              </button>
                            )}
                          </div>
                          <button className="mp-guide-toggle" onClick={() => setExpandedGuide(isGuide?null:apt.id)}>
                            {isGuide?"▲":"▼"} How to join
                          </button>
                          {isGuide && (
                            <div className="mp-guide-body">
                              {[
                                ["Be in a quiet private space","📶"],
                                [`Join opens at ${(()=>{const d=buildSessionDate(apt.date,apt.time);d.setMinutes(d.getMinutes()-5);return d.toLocaleTimeString("en-IN",{hour:"2-digit",minute:"2-digit"});})()}, 5 min early`,"⏰"],
                                ["Click Chat or Video to start","🖥️"],
                                ["Allow mic & camera when prompted","🎤"],
                                ["Session runs 60 minutes","⏱️"],
                                ["Click ✅ Confirm Done after","✅"],
                                ["Rate your experience when done","⭐"],
                              ].map(([text,icon],i) => (
                                <div key={i} className="mp-guide-step">
                                  <div className="mp-guide-num">{i+1}</div>
                                  <div>{icon} {text}</div>
                                </div>
                              ))}
                            </div>
                          )}
                          <div className="mp-receipt" style={{marginTop:10}}>
                            <div className="mp-receipt-row"><span>Session</span><span>₹{apt.session_price}</span></div>
                            <div className="mp-receipt-row"><span>Platform</span><span>₹{apt.platform_fee||PLATFORM_FEE}</span></div>
                            <div className="mp-receipt-row total"><span>Total paid</span><span>₹{apt.total_paid}</span></div>
                          </div>
                          {canCancel(apt) && (
                            <div className="mp-cancel-section">
                              <div className="mp-cancel-preview">
                                <span>{hrs>12?"✅ Free cancellation":"⚠️ Late cancellation"}</span>
                                <span>
                                  {hrs>12
                                    ? <span className="refund">Refund: ₹{refund.refund}</span>
                                    : <><span className="fee">Fee: ₹{refund.fee}</span> · <span className="refund">₹{refund.refund} back</span></>}
                                </span>
                              </div>
                              <button className="mp-cancel-btn" onClick={() => {setCancelAppt(apt);setShowCancel(true);}}>❌ Cancel Session</button>
                            </div>
                          )}
                        </div>
                      );
                    })
              )}

              {/* COMPLETED */}
              {sessionsTab==="completed" && (
                completed.length===0
                  ? <div className="mp-empty-state"><span className="mp-empty-state-icon">✅</span>No completed sessions yet</div>
                  : completed.map(apt => (
                    <div key={apt.id} className="mp-apt done">
                      <div className="mp-apt-top">
                        <div className="mp-apt-name">{apt.mentorName}</div>
                        <span className="mp-apt-badge done">Completed</span>
                      </div>
                      <div className="mp-apt-info">📅 {apt.date} at {formatDisplayTime(apt.time)}</div>
                      <div className="mp-apt-topic">📝 {apt.topic}</div>
                      <div className="mp-receipt">
                        <div className="mp-receipt-row"><span>Session</span><span>₹{apt.session_price}</span></div>
                        <div className="mp-receipt-row"><span>Platform</span><span>₹{apt.platform_fee||PLATFORM_FEE}</span></div>
                        <div className="mp-receipt-row total"><span>Total paid</span><span>₹{apt.total_paid}</span></div>
                      </div>
                      <div className="mp-apt-actions">
                        {!apt.session_confirmed_by_user && (
                          <button className="mp-confirm-btn" onClick={() => confirmSession(apt.id)} disabled={confirmLoading}>
                            {confirmLoading?"…":"✅ Confirm Done"}
                          </button>
                        )}
                        {apt.status!=="completed_with_feedback" && (
                          <button className="mp-fb-btn" onClick={() => {setFeedbackData({rating:0,review:"",mentorId:apt.mentor_id||apt.mentor?.id,appointmentId:apt.id});setShowFeedback(true);}}>⭐ Rate</button>
                        )}
                        <button className="mp-rpt-btn" onClick={() => {setReportData({category:"",description:"",appointmentId:apt.id,mentorId:apt.mentor_id||apt.mentor?.id});setShowReport(true);}}>🚩 Report</button>
                      </div>
                    </div>
                  ))
              )}

              {/* CANCELLED */}
              {sessionsTab==="cancelled" && (
                cancelled.length===0
                  ? <div className="mp-empty-state"><span className="mp-empty-state-icon">❌</span>No cancelled sessions</div>
                  : cancelled.map(apt => (
                    <div key={apt.id} className="mp-apt xed">
                      <div className="mp-apt-top">
                        <div className="mp-apt-name">{apt.mentorName}</div>
                        <span className="mp-apt-badge xed">Cancelled</span>
                      </div>
                      <div className="mp-apt-info">📅 {apt.date} at {formatDisplayTime(apt.time)}</div>
                      <div className="mp-apt-topic">📝 {apt.topic}</div>
                      <div className="mp-receipt">
                        <div className="mp-receipt-row"><span>Refund</span><span>₹{apt.refund_amount||0}</span></div>
                        <div className="mp-receipt-row"><span>Status</span><span>{apt.refund_status||"—"}</span></div>
                      </div>
                      {apt.cancellation_reason && <div style={{fontSize:".76rem",color:"var(--muted)",marginTop:4}}>Reason: {apt.cancellation_reason}</div>}
                    </div>
                  ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* ─── CHAT / VIDEO MODAL ─── */}
      {showChat && activeChat && (
        <div className="mp-overlay" onClick={e => e.target===e.currentTarget&&setShowChat(false)}>
          <div className="mp-chat-modal">
            <div className="mp-modal-handle"/>
            <div className="mp-chat-hdr">
              <div>
                <div style={{fontFamily:"var(--font-display)",fontWeight:700,fontSize:".95rem",color:"var(--ink)"}}>
                  {activeChat.appointment?.mentorName||activeChat.appointment?.mentor_name}
                </div>
                <div style={{fontSize:".76rem",color:"var(--muted)",marginTop:1}}>📝 {activeChat.appointment?.topic}</div>
              </div>
              <div style={{display:"flex",gap:8,alignItems:"center"}}>
                {timeLeft!==null && timeLeft>0 && (
                  <div className={`mp-chat-timer${timeLeft<300000?" warn":""}`}>⏰ {formatTimeRemaining(timeLeft)}</div>
                )}
                <button className="mp-modal-close" onClick={() => {if(timerRef.current)clearInterval(timerRef.current);setShowChat(false);activeChatRef.current=null;}}>✕</button>
              </div>
            </div>
            <div className="mp-ctabs">
              <button className={`mp-ctab${chatTab==="chat"?" active":""}`} onClick={() => setChatTab("chat")}>💬 Chat</button>
              <button className={`mp-ctab${chatTab==="video"?" active":""}`} onClick={() => setChatTab("video")}>📹 Video</button>
            </div>
            {chatTab==="chat" && (
              <>
                <div className="mp-msgs">
                  {chatMsgs.length===0
                    ? <div style={{textAlign:"center",color:"var(--muted)",marginTop:40,fontSize:".84rem"}}>Session started — say hello! 👋</div>
                    : chatMsgs.map((msg,i) => (
                      <div key={msg.id||i} className={`mp-msg ${msg.sender_role==="user"?"sent":"recv"}`}>
                        <div className="mp-msg-bbl">{msg.message}</div>
                        <div className="mp-msg-time">{new Date(msg.created_at).toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"})}</div>
                      </div>
                    ))}
                  <div ref={endRef}/>
                </div>
                <div className="mp-input-row">
                  <input placeholder="Write something kind…" value={newMsg} onChange={e => setNewMsg(e.target.value)} onKeyDown={e => e.key==="Enter"&&!e.shiftKey&&sendMessage()}/>
                  <button className="mp-send" onClick={sendMessage}>Send</button>
                </div>
              </>
            )}
            {chatTab==="video" && (
              <div className="mp-video-wrap">
                <HealHubVideoRoom
                  roomId={activeChat.appointment?.video_room_id}
                  participantName={auth.currentUser?.displayName||auth.currentUser?.email||"User"}
                  role="VIEWER"
                  sessionEndTime={activeChat.scheduled_end}
                  onLeave={() => setChatTab("chat")}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* ─── CANCEL CONFIRM ─── */}
      {showCancel && cancelAppt && (
        <div className="mp-overlay" onClick={e => e.target===e.currentTarget&&!cancelLoading&&setShowCancel(false)}>
          <div className="mp-modal">
            <div className="mp-modal-handle"/>
            <div className="mp-modal-hdr">
              <div className="mp-modal-title">Cancel Session</div>
              <button className="mp-modal-close" onClick={() => !cancelLoading&&setShowCancel(false)}>✕</button>
            </div>
            <div className="mp-modal-body">
              {(() => {
                const hrs=getHrs(cancelAppt), calc=calcCancellationRefund(cancelAppt.session_price,hrs);
                return (
                  <>
                    <div className="mp-apt" style={{marginBottom:14}}>
                      <div className="mp-apt-name">{cancelAppt.mentorName}</div>
                      <div className="mp-apt-info">📅 {cancelAppt.date} at {formatDisplayTime(cancelAppt.time)}</div>
                      <div className="mp-apt-topic">📝 {cancelAppt.topic}</div>
                    </div>
                    <div className="mp-price-box">
                      <div className="mp-price-row"><span>You paid</span><span>₹{cancelAppt.total_paid}</span></div>
                      <div className="mp-price-row"><span>Cancellation fee</span><span style={{color:"var(--danger)"}}>- ₹{calc.fee}</span></div>
                      <div className="mp-price-row"><span>Refund to you</span><span style={{color:"var(--success)"}}>₹{calc.refund}</span></div>
                    </div>
                    {calc.type==="late" && <div className="mp-alert error">⚠️ Cancelling within 12 hours — 70% deduction applies.</div>}
                    <div style={{fontSize:".76rem",color:"var(--muted)",marginBottom:16}}>Refund arrives in 5–7 business days.</div>
                    <div style={{display:"flex",gap:10}}>
                      <button className="mp-btn-ghost" style={{width:"auto",flex:1}} onClick={() => setShowCancel(false)}>Keep Session</button>
                      <button onClick={handleCancelConfirm} disabled={cancelLoading} className="mp-btn-danger solid" style={{flex:1,padding:12,borderRadius:"var(--r-md)",cursor:"pointer"}}>
                        {cancelLoading?<><span className="mp-spin"/> Cancelling…</>:"Confirm Cancel"}
                      </button>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* ─── FEEDBACK ─── */}
      {showFeedback && (
        <div className="mp-overlay" onClick={e => e.target===e.currentTarget&&setShowFeedback(false)}>
          <div className="mp-modal">
            <div className="mp-modal-handle"/>
            <div className="mp-modal-hdr">
              <div className="mp-modal-title">Rate Your Session ⭐</div>
              <button className="mp-modal-close" onClick={() => setShowFeedback(false)}>✕</button>
            </div>
            <div className="mp-modal-body">
              <div className="mp-fg">
                <label>Rating *</label>
                <div className="mp-star-row">
                  {[1,2,3,4,5].map(n => (
                    <button key={n} className="mp-star-btn" onClick={() => setFeedbackData(p => ({...p,rating:n}))}>
                      <span style={{color:n<=feedbackData.rating?"var(--peach)":"var(--sand)",fontSize:"1.9rem"}}>{n<=feedbackData.rating?"★":"☆"}</span>
                    </button>
                  ))}
                </div>
              </div>
              <div className="mp-fg">
                <label>Review (optional)</label>
                <textarea rows={3} placeholder="Share your experience…" value={feedbackData.review} onChange={e => setFeedbackData(p => ({...p,review:e.target.value}))}/>
              </div>
              <button className="mp-btn-primary" onClick={submitFeedback} disabled={fbLoading||!feedbackData.rating}>
                {fbLoading?<><span className="mp-spin"/> Submitting…</>:"Submit Feedback"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── REPORT ─── */}
      {showReport && (
        <div className="mp-overlay" onClick={e => e.target===e.currentTarget&&setShowReport(false)}>
          <div className="mp-modal">
            <div className="mp-modal-handle"/>
            <div className="mp-modal-hdr">
              <div className="mp-modal-title">Report Mentor 🚩</div>
              <button className="mp-modal-close" onClick={() => setShowReport(false)}>✕</button>
            </div>
            <div className="mp-modal-body">
              <div className="mp-fg"><label>Category *</label></div>
              <div className="mp-report-opts">
                {["Inappropriate content","Mentor did not show up","Unprofessional behaviour","Harassment or abuse","Suspected fake credentials","Other"].map(reason => (
                  <button key={reason} className={`mp-report-opt${reportData.category===reason?" on":""}`} onClick={() => setReportData(p => ({...p,category:reason}))}>{reason}</button>
                ))}
              </div>
              <div className="mp-fg">
                <label>Description *</label>
                <textarea rows={4} placeholder="Describe what happened…" value={reportData.description} onChange={e => setReportData(p => ({...p,description:e.target.value}))}/>
              </div>
              <div style={{fontSize:".76rem",color:"var(--muted)",marginBottom:14}}>Reports reviewed within 48 hours.</div>
              <button
                className="mp-btn-danger solid"
                style={{width:"100%",padding:14,borderRadius:"var(--r-md)",fontFamily:"var(--font)",fontWeight:700,fontSize:".9rem",cursor:"pointer"}}
                onClick={submitReport}
                disabled={!reportData.category||!reportData.description.trim()}
              >
                Submit Report
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── TOASTS ─── */}
      <div className="mp-toasts">
        {toasts.map(t => <div key={t.id} className={`mp-toast ${t.type}`}>{t.msg}</div>)}
      </div>
    </>
  );
}